/* 一般外科 LEVEL 1 — 晨間巡房（教學關；duodenal stump leak after subtotal gastrectomy, the pathway says "start water"） */
import {
  addWait,
  ask,
  C,
  type Cell,
  chat,
  clamp,
  clearWait,
  clock,
  closeDialog,
  closeZoom,
  doc,
  ent,
  eventBanner,
  findEnt,
  finding,
  getImagery,
  type Level,
  leaveBy,
  me,
  mkEnt,
  note,
  openDialog,
  openZoom,
  P,
  playEnding,
  post,
  ptext,
  R,
  renderSide,
  session,
  sessionGuard,
  setZBar,
  spawnAt,
  spend,
  strList,
  T,
  tr,
  walkEnt,
  zone,
  type ZoomCfg,
} from "../../engine";
import {
  drainHots,
  drawDrainBag,
  drawMapPatient,
  drawTPR,
  drawWardBed,
  FLUID,
  tprHots,
  type TprCol,
  type TprSheet,
  WARD_FURNITURE,
  wardHots,
} from "./ward";
import { photoOnly, sideMark } from "../shared/imaging";

/* ---------- static data ---------- */

const FACTS = {
  pain_site: tr(
    "痛的地方不太像傷口，是肚子上面偏右邊，悶悶脹脹的，半夜開始越來越痛",
    "It doesn’t really feel like the wound; it’s the upper belly, off to the right, a dull bloated ache that started getting worse in the middle of the night",
  ),
  pain_worse: tr(
    "比前兩天痛，咳嗽、翻身會更痛",
    "Hurts more than the last two days; coughing or turning over makes it worse",
  ),
  rigor: tr(
    "半夜冷到發抖、牙齒打顫，蓋了兩條被子還在抖",
    "In the middle of the night he was so cold he shook, teeth chattering, still shaking under two blankets",
  ),
  sob: tr("有點喘，吸不太到氣", "A bit short of breath, can’t quite get a full breath in"),
  bloat: tr("肚子脹脹的，有點想吐", "Belly feels bloated, a little nauseous"),
  flatus: tr("昨天下午有放屁", "Passed gas yesterday afternoon"),
  urine: tr("尿好像變少、顏色比較深", "Seems to be peeing less, and it’s darker"),
  nosleep: tr("一整晚幾乎沒睡", "Barely slept all night"),
  wedding: tr(
    "下週六孫子結婚，想趕快吃東西、出院",
    "His grandson gets married next Saturday; he wants to eat soon and go home",
  ),
  hide: tr(
    "他叫女兒不要跟醫生講發抖的事，怕又要做檢查、出不了院",
    "He told his daughter not to tell the doctors about the shaking, afraid of more tests and not getting discharged",
  ),
  z_void: tr("昨晚跟今天早上都有自己尿尿，很順", "Peed on his own last night and this morning, no trouble"),
  z_pain: tr("傷口痛大概 2 到 3 分，吃止痛藥就好", "Wound pain about 2 to 3 out of 10; the pain pills take care of it"),
  z_scrotum: tr("右邊陰囊有一點點腫，不太痛", "Right side of the scrotum is a little swollen, not very painful"),
  z_move: tr("明天要幫朋友搬家", "Tomorrow he’s helping a friend move house"),
  z_work: tr("做物流，平常要搬貨", "Works in logistics; lifts and hauls freight for a living"),
  t_walk: tr(
    "早上已經自己推點滴架在走廊走了兩圈",
    "Already walked two laps of the hallway this morning, pushing his own IV pole",
  ),
  t_pain: tr("傷口痛 3 分，可以忍", "Wound pain 3 out of 10, bearable"),
  t_eat: tr(
    "昨天開始喝水沒有不舒服，有放屁，想吃東西",
    "Started drinking water yesterday with no trouble, passing gas, wants to eat",
  ),
  t_hbv: tr(
    "B 肝帶原，平常每天吃貝樂克（Entecavir），住院後就沒吃了，想問要不要繼續吃",
    "Hepatitis B carrier; takes Baraclude (entecavir) every day at home but hasn’t had it since admission; wants to ask whether he should keep taking it",
  ),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

const TEACH = {
  wound: tr("傷口照護與洗澡", "Wound care & showering"),
  activity: tr("活動與提重物限制", "Activity & lifting limits"),
  pain: tr("止痛藥怎麼吃", "How to take pain meds"),
  warn: tr("什麼情況要提早回來", "When to come back early"),
  opd: tr("回診時間", "Follow-up visit"),
} as const;
type TeachId = keyof typeof TEACH;
const isTeach = (x: string): x is TeachId => x in TEACH;

const ORD = {
  npo: tr("陳伯伯禁食（NPO）", "NPO for Mr. Chen"),
  vitals: tr("請護理師重量陳伯伯生命徵象", "Recheck Mr. Chen’s vitals"),
  labs: tr("加驗：CRP、肝腎功能、lactate（急件）", "Add-on: CRP, LFTs, renal panel, lactate (stat)"),
  bcx: tr("血液培養兩套", "Blood cultures x2"),
  drainlab: tr("引流液送 bilirubin、amylase", "Drain fluid for bilirubin, amylase"),
  uo: tr("每小時記尿量", "Hourly urine output"),
  o2: tr("鼻導管氧氣 2 L/min", "Nasal cannula O2 2 L/min"),
  ivf: tr("LR 500 mL 快速輸注", "LR 500 mL bolus"),
  tazo: "Piperacillin/tazobactam 4.5 g IV q6h",
  cromtz: "Ceftriaxone 2 g + Metronidazole 500 mg IV",
  cef: "Cefazolin 1 g IV q8h",
  ct: tr("腹部電腦斷層（含顯影劑）", "CT abdomen with contrast"),
} as const;
type OrderId = keyof typeof ORD;
const isOrder = (x: string): x is OrderId => x in ORD;
/** Orders a nurse / NP can carry out without a doctor's go-ahead. */
const NURSE_OK: ReadonlySet<OrderId> = new Set(["npo", "vitals", "labs", "bcx", "drainlab", "uo", "o2"]);
const ABX: readonly OrderId[] = ["tazo", "cromtz", "cef"];

type PtId = "chen" | "zhiwei" | "tsai";
const PT_NAME: Record<PtId, string> = {
  chen: tr("陳伯伯", "Mr. Chen"),
  zhiwei: tr("志偉", "Chih-Wei"),
  tsai: tr("蔡先生", "Mr. Tsai"),
};

type SeenId =
  | "tpr_chen"
  | "tpr_tsai"
  | "tpr_zhiwei"
  | "pulse_chen"
  | "io_chen"
  | "drainrow_chen"
  | "drain_chen"
  | "drain_tsai"
  | "site_chen"
  | "wound_chen"
  | "belly_chen"
  | "lung_chen"
  | "cbc_chen"
  | "note_chen"
  | "op_chen"
  | "meds_tsai"
  | "pathway"
  | "pathway_fine"
  | "census";

type Headline = "lead" | "buried" | "none";
const isHeadline = (x: unknown): x is Headline => x === "lead" || x === "buried" || x === "none";

interface Vitals {
  hr: number;
  sbp: number;
  dbp: number;
  rr: number;
  temp: number;
  spo2: number;
}

interface Order {
  at: number;
  ready: number;
  done: boolean;
}

type Conf = "wait" | "ready" | "open" | "done";

interface Vars {
  revealed: Set<FactId>;
  taught: Set<TeachId>;
  seen: Set<SeenId>;
  handover: boolean;
  orders: Partial<Record<OrderId, Order>>;
  npoAt: number | null;
  drankAt: number | null;
  /** Clerk told someone to hold the water. */
  held: boolean;
  /** Clerk okayed the water. */
  okedWater: boolean;
  fluidAt: number | null;
  abxAt: number | null;
  abxDrug: OrderId | null;
  cultureBeforeAbx: boolean | null;
  rApproved: boolean;
  rApprovedAt: number | null;
  kit: boolean;
  /** Whose germs are on the clerk's hands: a patient, or "clean" after sanitising. */
  hands: PtId | "clean" | null;
  handSlips: number;
  vitals: { who: PtId; t: number; v: Vitals }[];
  rigorAt: number | null;
  rigorSeen: boolean;
  vomitAt: number | null;
  shockAt: number | null;
  zhiweiOut: boolean;
  dischargeBy: "clerk" | "np" | null;
  etvFixed: boolean;
  tsaiInBed: boolean;
  conf: Conf;
  confOpenAt: number | null;
  headline: Headline | null;
  confTalks: number;
  accepted: boolean;
  acceptedAt: number | null;
  acceptedVia: "conf" | "phone" | "r" | null;
  rPresented: boolean;
  vsHere: boolean;
  vsGone: boolean;
  ctAt: number | null;
  endAt: number | null;
}

const S = session<Vars>();

const DOOR: Cell = [19, 6];
const CHEN_BED: Cell = [6, 1];
const ZHIWEI_BED: Cell = [2, 1];
const TSAI_BED: Cell = [13, 9];
const RIGOR_T = 17;
const WATER_T = 25;
const VS_ARRIVE_T = 23;
const CONF_T = 30;
const R_PRESENT_T = 35;
const CONF_END_T = 41;

/* ---------- 陳伯伯's physiology ---------- */

const ramp = (from: number | null, t: number, over: number): number =>
  from === null ? 0 : clamp((t - from) / over, 0, 1);

function chenV(t: number = S.t): Vitals {
  const drink = ramp(S.drankAt, t, 10);
  const fluid = ramp(S.fluidAt, t, 10);
  const goodAbx = S.abxDrug !== null && S.abxDrug !== "cef" && S.abxAt !== null;
  // Blood pressure drifts down minute by minute; treatment flattens the slope from when it starts.
  let drop = 0;
  let climb = 0;
  for (let i = 1; i <= t; i++) {
    let s = 0.72;
    let h = 0.35;
    if (goodAbx && i > (S.abxAt ?? 0) + 5) {
      s -= 0.35;
      h -= 0.2;
    }
    if (S.acceptedAt !== null && i > S.acceptedAt) {
      s -= 0.3;
      h -= 0.15;
    }
    drop += Math.max(0.05, s);
    climb += Math.max(0, h);
  }
  const sbp = Math.round(122 - drop - 7 * drink + 12 * fluid);
  const hr = Math.round(116 + climb + 9 * drink - 8 * fluid);
  const rig = S.rigorAt !== null && t >= S.rigorAt;
  return {
    hr,
    sbp,
    dbp: Math.round(sbp * 0.58),
    rr: Math.round(24 + t / 12 + 2 * drink),
    temp: Number((rig ? 38.7 : 37.9 + t * 0.008).toFixed(1)),
    spo2: Math.round(95 - t / 25 - drink + (S.orders.o2 ? 2 : 0)),
  };
}

function vitalsOf(who: PtId): Vitals {
  if (who === "chen") return chenV();
  if (who === "tsai") return { hr: 80, sbp: 128, dbp: 78, rr: 16, temp: 37.3, spo2: 98 };
  return { hr: 70, sbp: 122, dbp: 74, rr: 14, temp: 36.7, spo2: 99 };
}

const vtxt = (v: Vitals): string =>
  tr(
    `BP ${v.sbp}/${v.dbp}、HR ${v.hr}、RR ${v.rr}、BT ${v.temp}°C、SpO2 ${v.spo2}%`,
    `BP ${v.sbp}/${v.dbp}, HR ${v.hr}, RR ${v.rr}, BT ${v.temp}°C, SpO2 ${v.spo2}%`,
  );

const drainMl = (t: number = S.t): number =>
  Math.round(280 + 2.2 * t + (S.drankAt !== null ? 3 * Math.max(0, t - S.drankAt) : 0));

/* ---------- evidence & knowledge ---------- */

const measured = (who: PtId): { t: number; v: Vitals }[] => S.vitals.filter((x) => x.who === who);

/** Independent pieces of evidence for a leak the clerk actually has in hand. */
function evidence(): string[] {
  const out: string[] = [];
  if (S.seen.has("pulse_chen") || measured("chen").some((x) => x.v.hr >= 110))
    out.push(tr("心跳一路往上（>110）", "Heart rate climbing steadily (>110)"));
  if (measured("chen").some((x) => x.v.temp >= 38) || S.revealed.has("rigor") || S.rigorSeen)
    out.push(tr("發燒、畏寒發抖", "Fever, chills and rigors"));
  if (S.seen.has("drain_chen") || S.seen.has("drainrow_chen"))
    out.push(tr("引流液變混濁、偏膽汁色、量增加", "Drain fluid cloudy, bile-tinged, output up"));
  if (S.seen.has("belly_chen")) out.push(tr("右上腹壓痛合併 guarding", "RUQ tenderness with guarding"));
  if (S.seen.has("cbc_chen")) out.push(tr("WBC 17,900、band 7%", "WBC 17,900, bands 7%"));
  if (S.orders.drainlab?.done) out.push(tr("引流液 bilirubin 遠高於血清", "Drain bilirubin far above serum"));
  if (S.seen.has("wound_chen")) out.push(tr("傷口本身乾淨（不是傷口痛）", "Wound itself clean (not wound pain)"));
  return out;
}

function known(): string {
  const out: string[] = [];
  const vs = S.vitals.map((x) => `${clock(x.t)} ${PT_NAME[x.who]} ${vtxt(x.v)}`);
  out.push(tr(`自己量過的生命徵象：${vs.join("；") || "沒有"}`, `Vitals the clerk took: ${vs.join("; ") || "none"}`));
  out.push(
    tr(
      `看過的資料：${
        [
          S.handover && "夜班交班",
          S.seen.has("tpr_chen") && "陳伯伯 TPR 單",
          S.seen.has("pulse_chen") && "（注意到脈搏從 84 一路升到 116）",
          S.seen.has("io_chen") && "（注意到大夜尿量只有 310 mL）",
          S.seen.has("drainrow_chen") && "（注意到大夜引流 280 mL、顏色變了）",
          S.seen.has("note_chen") && "陳伯伯夜班護理紀錄",
          S.seen.has("cbc_chen") && "陳伯伯今早 CBC",
          S.seen.has("op_chen") && "陳伯伯手術紀錄",
          S.seen.has("pathway") && "胃切除臨床路徑",
          S.seen.has("pathway_fine") && "（看到路徑的暫停條件）",
          S.seen.has("tpr_tsai") && "蔡先生 TPR 單",
          S.seen.has("meds_tsai") && "蔡先生用藥",
          S.seen.has("census") && "病房白板",
        ]
          .filter(Boolean)
          .join("、") || "沒有"
      }`,
      `Records reviewed: ${
        [
          S.handover && "night-shift handoff",
          S.seen.has("tpr_chen") && "Mr. Chen's TPR sheet",
          S.seen.has("pulse_chen") && "(noticed the pulse climbing from 84 to 116)",
          S.seen.has("io_chen") && "(noticed night-shift urine output was only 310 mL)",
          S.seen.has("drainrow_chen") && "(noticed 280 mL of drain output overnight, color changed)",
          S.seen.has("note_chen") && "Mr. Chen's night nursing notes",
          S.seen.has("cbc_chen") && "Mr. Chen's CBC this morning",
          S.seen.has("op_chen") && "Mr. Chen's op note",
          S.seen.has("pathway") && "gastrectomy clinical pathway",
          S.seen.has("pathway_fine") && "(saw the pathway's hold criteria)",
          S.seen.has("tpr_tsai") && "Mr. Tsai's TPR sheet",
          S.seen.has("meds_tsai") && "Mr. Tsai's medications",
          S.seen.has("census") && "ward whiteboard",
        ]
          .filter(Boolean)
          .join(", ") || "none"
      }`,
    ),
  );
  out.push(
    tr(
      `檢查發現：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Exam findings: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `已開醫囑：${
        (Object.entries(S.orders) as [OrderId, Order][])
          .map(([k, o]) => ORD[k] + (o.done ? "（已完成）" : "（進行中）"))
          .join("、") || "無"
      }`,
      `Orders placed: ${
        (Object.entries(S.orders) as [OrderId, Order][])
          .map(([k, o]) => ORD[k] + (o.done ? " (done)" : " (in progress)"))
          .join(", ") || "none"
      }`,
    ),
  );
  out.push(
    tr(
      `問出的病史：${[...S.revealed].map((id) => FACTS[id]).join("；") || "無"}`,
      `History obtained: ${[...S.revealed].map((id) => FACTS[id]).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `陳伯伯的飲食：${S.drankAt !== null ? `${clock(S.drankAt)} 開始喝水` : S.npoAt !== null ? `${clock(S.npoAt)} 起禁食` : "還沒決定"}`,
      `Mr. Chen's diet: ${S.drankAt !== null ? `started water at ${clock(S.drankAt)}` : S.npoAt !== null ? `NPO since ${clock(S.npoAt)}` : "not decided yet"}`,
    ),
  );
  out.push(
    tr(
      `志偉出院衛教：${[...S.taught].map((k) => TEACH[k]).join("、") || "還沒做"}`,
      `Chih-Wei's discharge teaching: ${[...S.taught].map((k) => TEACH[k]).join(", ") || "not done yet"}`,
    ),
  );
  return out.join("\n");
}

const reveal = (ids: unknown): void => {
  for (const id of strList(ids)) if (isFact(id)) S.revealed.add(id);
};

/* ---------- hand hygiene ---------- */

function touch(who: PtId, ch: string): void {
  if (S.hands === who) return;
  if (S.hands !== "clean") {
    S.handSlips++;
    if (S.handSlips === 1)
      post(
        ch,
        "npc",
        "gs_nurse",
        tr(
          "學弟，摸病人前先乾洗手喔。床尾就有。",
          "Hey, sanitize your hands before you touch the patient. There’s a dispenser at the foot of the bed.",
        ),
      );
    else if (S.handSlips === 3)
      post(
        ch,
        "npc",
        "np",
        tr("你今天從一床摸到二床都沒洗手欸。", "You’ve gone from bed one to bed two without washing your hands today."),
      );
  }
  S.hands = who;
}

function wash(): void {
  S.hands = "clean";
  note(tr("你按了兩下乾洗手，搓滿二十秒。", "You pump the hand sanitizer twice and rub for a full twenty seconds."));
}

/* ---------- exam ---------- */

function takeVitals(who: PtId, ch: string): void {
  touch(who, ch);
  const v = vitalsOf(who);
  S.vitals.push({ who, t: S.t, v });
  me(ch, tr(`（幫${PT_NAME[who]}量生命徵象）`, `(Takes ${PT_NAME[who]}’s vitals)`));
  post(ch, "result", null, vtxt(v));
  if (who === "chen" && v.temp >= 38) finding(tr("體溫", "Temp"), `${clock()} ${v.temp}°C`);
  spend(2);
}

function find(ch: string, label: string, name: string, txt: string, cost = 1): void {
  me(ch, tr(`（${label}）`, `(${label})`));
  if (!S.findings.some((f) => f.name === name && f.text === txt)) finding(name, txt);
  post(ch, "narr", null, txt);
  spend(cost);
}

function chenBelly(): void {
  touch("chen", "chen");
  S.seen.add("belly_chen");
  const worse = S.drankAt !== null && S.t >= S.drankAt + 4;
  find(
    "chen",
    tr("摸陳伯伯的肚子", "Palpates Mr. Chen’s abdomen"),
    tr("腹部", "Abdomen"),
    worse
      ? tr(
          "肚子比剛剛更硬。右上腹一碰就縮，整個上腹都有 guarding，腸音幾乎聽不到。他咬著牙：「剛喝完水就更痛了……」",
          "His belly is harder than before. He flinches the moment you touch the RUQ, there’s guarding across the whole upper abdomen, and bowel sounds are almost gone. He grits his teeth: “It got worse right after I drank that water…”",
        )
      : tr(
          "上腹偏右壓痛很明顯，右上腹有 guarding；左下腹軟。腸音減少。他皺眉：「那邊……不是傷口那裡痛，是裡面。」",
          "Marked tenderness in the upper abdomen, right of midline, with guarding in the RUQ; the left lower quadrant is soft. Bowel sounds decreased. He frowns: “There… it’s not where the cut is. It’s inside.”",
        ),
    2,
  );
}

function chenWound(): void {
  if (!S.kit) {
    note(
      tr(
        "要打開敷料，先去護理站的換藥車拿換藥包。",
        "To open the dressing, first grab a dressing kit from the dressing cart by the nurses’ station.",
      ),
      "chen",
    );
    return;
  }
  touch("chen", "chen");
  S.seen.add("wound_chen");
  find(
    "chen",
    tr("打開上腹正中傷口的敷料", "Opens the dressing over the upper midline incision"),
    tr("傷口", "Wound"),
    tr(
      "上腹正中傷口乾淨，縫線完整，沒有紅腫、沒有滲液，壓傷口周圍他不太痛。——痛的不是傷口。",
      "The upper midline incision is clean, sutures intact, no redness, no drainage; pressing around it barely bothers him. It isn’t the wound that hurts.",
    ),
    2,
  );
}

function chenLung(): void {
  touch("chen", "chen");
  S.seen.add("lung_chen");
  find(
    "chen",
    tr("聽陳伯伯的肺", "Listens to Mr. Chen’s lungs"),
    tr("肺部", "Lungs"),
    tr(
      "呼吸淺快，右下肺呼吸音稍微減少，沒有明顯囉音。",
      "Breathing fast and shallow; breath sounds slightly decreased at the right base, no obvious crackles.",
    ),
    1,
  );
}

function zhiweiExam(): void {
  touch("zhiwei", "zhiwei");
  find(
    "zhiwei",
    tr("看志偉的傷口和右側鼠蹊", "Checks Chih-Wei’s incisions and right groin"),
    tr("志偉傷口", "Chih-Wei’s incisions"),
    tr(
      "肚臍與下腹三個小傷口，防水敷料乾淨。右側陰囊輕微腫脹、軟、不太痛，沒有發紅。肚子軟。",
      "Three small incisions at the umbilicus and lower abdomen, waterproof dressings clean. Right scrotum mildly swollen, soft, not very tender, no redness. Belly soft.",
    ),
    2,
  );
}

function tsaiExam(): void {
  touch("tsai", "tsai");
  find(
    "tsai",
    tr("摸蔡先生的肚子、看傷口", "Examines Mr. Tsai’s abdomen and wound"),
    tr("蔡先生腹部", "Mr. Tsai’s abdomen"),
    tr(
      "右肋下斜切口（倒 L 型）乾淨，肚子軟，傷口旁輕壓痛，沒有 guarding。腸音正常。",
      "Right subcostal oblique incision (reverse-L) is clean; belly soft, mild tenderness beside the wound, no guarding. Bowel sounds normal.",
    ),
    2,
  );
}

/* ---------- orders ---------- */

function needsDoctor(id: OrderId): boolean {
  return !NURSE_OK.has(id) && !S.rApproved && !S.accepted;
}

const ORDER_ETA: Partial<Record<OrderId, number>> = { labs: 20, bcx: 5, drainlab: 30, vitals: 2, ct: 30 };

function placeOrder(id: OrderId, ch: string, by: "gs_nurse" | "np"): void {
  if (S.orders[id] && id !== "vitals") {
    note(tr(`${ORD[id]}：已經在做了。`, `${ORD[id]}: already in progress.`), ch);
    return;
  }
  if (needsDoctor(id)) {
    post(
      ch,
      "npc",
      by,
      by === "np"
        ? tr(
            "這個要醫師同意喔。你去說服阿凱，或晨會直接跟主任講。我不會幫你亂開。",
            "That needs a doctor’s sign-off. Go convince Kai, or tell Dr. Chiang straight out at conference. I’m not ordering random stuff for you.",
          )
        : tr(
            "這要阿凱學長或主任開醫囑耶，我不能自己給。",
            "Kai or Dr. Chiang has to order that. I can’t just give it myself.",
          ),
    );
    return;
  }
  if (ABX.includes(id)) {
    const other = ABX.find((a) => a !== id && S.orders[a]);
    if (other) {
      post(ch, "npc", by, tr(`已經有掛 ${ORD[other]} 了喔。`, `He’s already got ${ORD[other]} running.`));
      return;
    }
  }
  me(ch, tr(`（${ORD[id]}）`, `(${ORD[id]})`));
  const eta = ORDER_ETA[id] ?? 1;
  S.orders[id] = { at: S.t, ready: S.t + eta, done: false };
  if (id === "npo") {
    S.npoAt ??= S.t;
    S.held = true;
    clearWait("water");
    post(
      ch,
      "npc",
      by,
      tr(
        "好，床頭掛 NPO 了，我也跟雅婷講一聲先不要給他喝。",
        "Okay, NPO sign’s up at the bed, and I’ll tell Ya-Ting not to give him anything to drink for now.",
      ),
    );
  } else if (id === "ivf") {
    S.fluidAt = S.t;
    post(ch, "npc", by, tr("LR 500 全速掛上去了。", "LR 500 is up and running wide open."));
  } else if (ABX.includes(id)) {
    S.abxAt = S.t;
    S.abxDrug = id;
    S.cultureBeforeAbx = !!S.orders.bcx;
    post(
      ch,
      "npc",
      by,
      S.orders.bcx
        ? tr(`${ORD[id]} 掛上去了，culture 剛剛已經抽了。`, `${ORD[id]} is hung. Cultures were drawn just before.`)
        : tr(
            `${ORD[id]} 掛上去了。……欸，culture 還沒抽耶。`,
            `${ORD[id]} is hung. …Wait, cultures haven’t been drawn yet.`,
          ),
    );
  } else if (id === "ct") {
    post(
      ch,
      "npc",
      by,
      tr(
        "CT 排好了，放射科說半小時內推下去。",
        "CT’s booked. Radiology says they’ll take him down within half an hour.",
      ),
    );
  } else if (id === "labs") post(ch, "npc", by, tr("急件送了，大概二十分鐘。", "Sent stat. About twenty minutes."));
  else if (id === "bcx")
    post(ch, "npc", by, tr("兩套，左右手各一套，我去抽。", "Two sets, one from each arm. I’ll go draw them."));
  else if (id === "drainlab")
    post(
      ch,
      "npc",
      by,
      tr(
        "引流液我無菌取了一管，送生化，大概半小時。",
        "I took a sterile sample of the drain fluid and sent it to chemistry. About half an hour.",
      ),
    );
  else if (id === "uo")
    post(ch, "npc", by, tr("好，我給他尿壺，每小時記。", "Okay, I’ll give him a urinal and chart it every hour."));
  else if (id === "o2") post(ch, "npc", by, tr("鼻導管 2 L 戴上了。", "Nasal cannula’s on at 2 L."));
  else if (id === "vitals") post(ch, "npc", by, tr("我等一下去量。", "I’ll go check them in a minute."));
  spend(1);
}

/* ---------- the water trap ---------- */

function drink(by: string): void {
  if (S.drankAt !== null || S.npoAt !== null || S.shockAt !== null) return;
  S.drankAt = S.t;
  clearWait("water");
  post(
    "chen",
    "narr",
    null,
    tr(
      `${by}倒了半杯溫開水，陳伯伯小口小口喝完：「終於可以喝了。」`,
      `${by} pours half a cup of warm water, and Mr. Chen sips it down: “Finally, I can drink.”`,
    ),
  );
}

/* ---------- 江主任 & 晨會 ---------- */

function vsArrive(): void {
  if (S.vsHere) return;
  S.vsHere = true;
  spawnAt("gs_vs", "vs", DOOR, [16, 2]);
  const v = ent("gs_vs");
  v.onArrive = () => {
    v.face = "left";
  };
  post(
    "sys",
    "narr",
    null,
    tr(
      "電梯打開，江主任提著公事包走進會議室。",
      "The elevator opens, and Dr. Chiang walks into the conference room with his briefcase.",
    ),
  );
  if (S.conf === "wait") S.conf = "ready";
}

function openConf(): void {
  if (S.conf !== "ready") return;
  S.conf = "open";
  S.confOpenAt = S.t;
  clearWait("conf");
  closeZoom();
  eventBanner(tr("晨會", "Morning conference"), tr("會議室・江主任", "Conference room · Dr. Chiang"));
  ask(
    "vs",
    "gs_vs",
    tr(
      "早。7 樓 clerk，你的三床。一床一句，先講要緊的。",
      "Morning. Seventh-floor clerk, your three beds. One line per bed, most urgent first.",
    ),
    {
      id: "present",
      text: tr("晨會：向江主任報告", "Conference: present to Dr. Chiang"),
      dlg: "vs",
    },
  );
  openDialog("vs");
}

function rPresent(): void {
  if (S.conf === "open" || S.conf === "done" || S.accepted) return;
  S.conf = "done";
  S.rPresented = true;
  clearWait("conf");
  if (S.rApproved) {
    post(
      "vs",
      "npc",
      "gs_r",
      tr(
        "Clerk 不知道跑哪去了，我先報。陳伯伯 POD4，昨晚開始心跳上來、發燒、引流液變混濁，懷疑 leak，檢查在做了。",
        "No idea where the clerk went, so I’ll go first. Mr. Chen, POD4: since last night his heart rate’s up, he’s febrile, the drain’s gone cloudy. We suspect a leak; workup’s underway.",
      ),
    );
    post(
      "vs",
      "npc",
      "gs_vs",
      tr(
        "Leak 就第一句講。CT 現在做，抗生素上，NPO。Clerk 呢？叫他晨會要到。",
        "A leak goes in the first sentence. CT now, start antibiotics, NPO. Where’s the clerk? Tell them to show up to conference.",
      ),
    );
    accept("r");
  } else {
    post(
      "vs",
      "npc",
      "gs_r",
      tr(
        "Clerk 不知道跑哪去了，我先報。志偉今天出院；陳伯伯 POD4 stable，照路徑今天開始喝水；蔡先生 POD2 拔 Foley、下床。",
        "No idea where the clerk went, so I’ll go first. Chih-Wei goes home today; Mr. Chen, POD4, stable, starts water today per pathway; Mr. Tsai, POD2, Foley out and up walking.",
      ),
    );
    post("vs", "npc", "gs_vs", tr("好。下一個病房。", "Fine. Next ward."));
    post(
      "sys",
      "narr",
      null,
      tr("晨會沒有人提到陳伯伯的心跳。", "Nobody at conference mentions Mr. Chen’s heart rate."),
    );
  }
}

function vsLeave(): void {
  if (S.vsGone || S.accepted) return;
  S.vsGone = true;
  S.conf = "done";
  const v = findEnt("gs_vs");
  if (v && !v.hidden) leaveBy(v, DOOR);
  const r = findEnt("gs_r");
  if (r && !r.hidden) leaveBy(r, DOOR);
  post(
    "sys",
    "narr",
    null,
    tr(
      "江主任和阿凱去刀房了，08:30 有一台 Whipple。",
      "Dr. Chiang and Kai head to the OR. There’s a Whipple at 08:30.",
    ),
  );
  if (S.dialog === "vs" || S.dialog === "r") closeDialog();
}

function accept(via: "conf" | "phone" | "r"): void {
  if (S.accepted) return;
  S.accepted = true;
  S.conf = "done";
  S.acceptedAt = S.t;
  S.acceptedVia = via;
  clearWait("present");
  eventBanner(tr("主任決定", "Attending’s call"), tr("NPO・抗生素・CT・準備引流", "NPO · Abx · CT · plan drainage"));
  S.npoAt ??= S.t;
  S.orders.bcx ??= { at: S.t, ready: S.t + 5, done: false };
  if (S.fluidAt === null) {
    S.fluidAt = S.t;
    S.orders.ivf = { at: S.t, ready: S.t + 1, done: false };
  }
  if (!S.abxDrug || S.abxDrug === "cef") {
    S.abxAt = S.t + 2;
    S.abxDrug = "tazo";
    S.orders.tazo = { at: S.t, ready: S.t + 1, done: false };
  }
  S.orders.ct ??= { at: S.t, ready: S.t + 30, done: false };
  S.endAt = S.t + (via === "phone" ? 3 : 4);
}

/* ---------- endings ---------- */

function ptLeaves(): void {
  post(
    "sys",
    "narr",
    null,
    tr(
      "傳送人員把陳伯伯的床推出病房，往放射科。",
      "Transport wheels Mr. Chen’s bed out of the ward, headed for radiology.",
    ),
  );
}

function endA(): void {
  void playEnding(
    "A",
    tr("晨會第一句", "The First Sentence"),
    [
      {
        who: "gs_vs",
        text: tr(
          "十二指腸殘端 leak 要想到。CT 看 collection，能打 pigtail 就先引流，不行就回刀房。",
          "Duodenal stump leak: you have to think of it. CT for a collection. If we can get a pigtail in, drain it first; if not, back to the OR.",
        ),
      },
      { who: "gs_r", text: tr("我去跟放射科講。", "I’ll call radiology."), fn: ptLeaves },
      {
        who: "chen_dau",
        text: tr("醫生……我爸是不是不能去婚禮了？", "Doctor… does this mean my dad can’t go to the wedding?"),
      },
      {
        who: "gs_vs",
        text: tr("先把命顧好。婚禮還有一週。", "Let’s keep him alive first. The wedding’s a week away."),
      },
      {
        who: "gs_vs",
        text: tr(
          "你第一句就講 leak。很好，外科要的就是這個。",
          "You led with the leak. Good. That’s what surgery needs.",
        ),
      },
    ],
    () =>
      tr(
        `你在 ${clock(S.acceptedAt ?? S.t)} 的晨會第一句就說出懷疑滲漏。陳伯伯禁食、抗生素、送 CT，沒有進展成休克。`,
        `You named a suspected leak in your very first sentence at the ${clock(S.acceptedAt ?? S.t)} conference. Mr. Chen was made NPO, started on antibiotics and sent to CT, and never went into shock.`,
      ),
  );
}

function endB(): void {
  const via = S.acceptedVia;
  void playEnding(
    "B",
    via === "phone"
      ? tr("電話報告", "Called It In")
      : via === "r"
        ? tr("學長替你講了", "Kai Said It for You")
        : tr("被主任打斷", "Cut Off by the Attending"),
    [
      {
        who: "gs_vs",
        text:
          via === "phone"
            ? S.vsGone
              ? tr(
                  "（擴音，刀房的背景聲）Leak 你現在才講？好，NPO、Tazocin、CT，我下了刀去看。",
                  "(On speaker, OR noise in the background) You’re only telling me about a leak now? Fine. NPO, Tazocin, CT. I’ll see him when I’m out of this case.",
                )
              : tr(
                  "好，NPO、Tazocin、culture、CT。我到了先去看他。晨會之前打來，這個電話打得好。",
                  "Okay. NPO, Tazocin, cultures, CT. I’ll see him first thing when I get in. Calling before conference: good call.",
                )
            : via === "r"
              ? tr(
                  "下次晨會要在。你看到的東西，要自己講出來。",
                  "Next time, be at conference. What you see, you say yourself.",
                )
              : tr(
                  "你繞了一大圈才講到重點。病人在燒、心跳一百二，這是第一句。",
                  "You took the long way around to the point. Patient’s febrile with a heart rate of 120: that’s your first sentence.",
                ),
      },
      { who: "gs_r", text: tr("CT 排了，我推他下去。", "CT’s booked. I’ll take him down."), fn: ptLeaves },
      {
        who: "chen_dau",
        text: tr(
          "謝謝……他昨晚一直發抖，我就覺得不對。",
          "Thank you… he was shaking all last night. I knew something wasn’t right.",
        ),
      },
    ],
    () =>
      tr(
        `陳伯伯在 ${clock(S.acceptedAt ?? S.t)} 開始照滲漏處理（${via === "phone" ? "電話報告" : via === "r" ? "由阿凱在晨會報告" : "晨會報告，重點沒有放在第一句"}）。${S.drankAt !== null ? "他在那之前已經照路徑喝了水。" : ""}`,
        `Mr. Chen was treated as a leak from ${clock(S.acceptedAt ?? S.t)} (${via === "phone" ? "reported by phone" : via === "r" ? "Kai presented him at conference" : "presented at conference, but not in the first sentence"}).${S.drankAt !== null ? " Before that, he had already started water per the pathway." : ""}`,
      ),
  );
}

function endC(): void {
  void playEnding(
    "C",
    tr("加護病房", "ICU"),
    [
      {
        who: "gs_nurse",
        text: tr("血壓 86/50，心跳 140，叫他反應變慢了！", "BP 86/50, HR 140, and he’s slow to respond!"),
      },
      {
        who: "gs_r",
        text: tr(
          "（跑進來）兩條大管路，LR 全速，Tazocin，抽 lactate，通知 ICU！",
          "(Runs in) Two large-bore IVs, LR wide open, Tazocin, draw a lactate, call the ICU!",
        ),
      },
      { who: "chen_dau", text: tr("爸！爸你看我！", "Dad! Dad, look at me!") },
      {
        who: "gs_vs",
        text: tr(
          "Septic shock。CT 做完直接回刀房。半夜心跳就上來了，有人看 TPR 嗎？",
          "Septic shock. Straight back to the OR after CT. His heart rate was up in the middle of the night. Did anyone look at the TPR?",
        ),
      },
      {
        text: tr(
          "陳伯伯轉進外科加護病房，下午回刀房清創、引流十二指腸殘端。",
          "Mr. Chen goes to the surgical ICU, and back to the OR that afternoon for washout and drainage of the duodenal stump.",
        ),
      },
    ],
    () =>
      tr(
        `陳伯伯在 ${clock(S.shockAt ?? S.t)} 進展成敗血性休克。${S.drankAt !== null ? `他在 ${clock(S.drankAt)} 照路徑開始喝水。` : ""}`,
        `Mr. Chen progressed to septic shock at ${clock(S.shockAt ?? S.t)}.${S.drankAt !== null ? ` He had started water per the pathway at ${clock(S.drankAt)}.` : ""}`,
      ),
  );
}

/* ---------- close-ups ---------- */

const PT_LOOK: Record<PtId, { hair: string; skin: string; sheet: string }> = {
  zhiwei: { hair: "#2a2228", skin: "#efc6a0", sheet: "#a9c8e8" },
  chen: { hair: "#b8b8c0", skin: "#e2b48c", sheet: "#a9c8e8" },
  tsai: { hair: "#3a3038", skin: "#d9ac84", sheet: "#a9c8e8" },
};

function chenDrainView(milking = false): Parameters<typeof drawDrainBag>[2] {
  return {
    color: S.drankAt !== null ? "#8a9a34" : "#93a03a",
    ml: drainMl(),
    cloudy: 0.6,
    flecks: true,
    tag: "#ffd447",
    siteStain: "#b8b040",
    milking,
  };
}

let milkUntil = 0;

function openDrain(who: "chen" | "tsai"): void {
  const back = (): void => {
    if (S.dialog === who) openZoom({ dlg: who, ...bedZoom(who) });
  };
  if (who === "chen") {
    touch("chen", "chen");
    S.seen.add("drain_chen");
    openZoom({
      dlg: "chen",
      title: tr("陳伯伯的引流袋", "Mr. Chen’s drain"),
      sub: tr("右側肝下、靠近十二指腸殘端（黃色標籤）", "Right subhepatic, near the duodenal stump (yellow tag)"),
      draw: (c, now) => drawDrainBag(c, now, chenDrainView(now < milkUntil)),
      hot: () =>
        drainHots({
          onFluid: () =>
            find(
              "chen",
              tr("看引流液", "Looks at the drain fluid"),
              tr("引流液", "Drain fluid"),
              tr(
                "混濁、黃綠色，像稀釋的膽汁，裡面有白色絮狀物。昨天白天的紀錄還是淡黃清澈。",
                "Cloudy, yellow-green, like diluted bile, with white flecks floating in it. Yesterday’s daytime record says pale yellow and clear.",
              ),
            ),
          onScale: () =>
            find(
              "chen",
              tr("看刻度", "Reads the scale"),
              tr("引流量", "Drain output"),
              tr(
                `袋子裡 ${drainMl()} mL（大夜倒過一次，00:00 起算）。昨天白班整班只有 25 mL。`,
                `${drainMl()} mL in the bag (emptied once overnight, counting from 00:00). Yesterday’s entire day shift was only 25 mL.`,
              ),
            ),
          onSite: () => {
            S.seen.add("site_chen");
            find(
              "chen",
              tr("看引流管出口的紗布", "Checks the gauze at the drain site"),
              tr("引流管出口", "Drain site"),
              tr(
                "出口的紗布被黃綠色液體染濕一圈，皮膚有點紅。",
                "The gauze at the exit site is soaked in a ring of yellow-green fluid; the skin is a little red.",
              ),
            );
          },
          onTube: () => {
            milkUntil = performance.now() + 1200;
            find(
              "chen",
              tr("擠引流管", "Milks the drain tube"),
              tr("引流管", "Drain tube"),
              tr(
                "管子通暢，擠完又流出一些黃綠色混濁液。",
                "The tube is patent; milking it brings out more cloudy yellow-green fluid.",
              ),
              1,
            );
          },
        }),
      bar: [
        C(
          tr("留檢體送 bilirubin、amylase", "Send fluid for bilirubin, amylase"),
          () => placeOrder("drainlab", "chen", "gs_nurse"),
          !!S.orders.drainlab,
        ),
      ],
      onBack: back,
    });
    return;
  }
  touch("tsai", "tsai");
  S.seen.add("drain_tsai");
  openZoom({
    dlg: "tsai",
    title: tr("蔡先生的引流袋", "Mr. Tsai’s drain"),
    sub: tr("右側橫膈下", "Right subphrenic"),
    draw: (c, now) =>
      drawDrainBag(c, now, { color: FLUID.serosang, ml: 40 + Math.round(S.t * 0.3), tag: "#7fb3e8", siteStain: null }),
    hot: () =>
      drainHots({
        onFluid: () =>
          find(
            "tsai",
            tr("看引流液", "Looks at the drain fluid"),
            tr("蔡先生引流", "Mr. Tsai’s drain"),
            tr(
              "淡紅色、清澈的血清樣液體，沒有膽汁色，沒有血塊。",
              "Pale red, clear serosanguineous fluid; no bile, no clots.",
            ),
          ),
        onScale: () =>
          find(
            "tsai",
            tr("看刻度", "Reads the scale"),
            tr("蔡先生引流量", "Mr. Tsai’s drain output"),
            tr(
              "00:00 起約 40 mL；前一天 90 → 60 mL，一天比一天少。",
              "About 40 mL since 00:00; the day before, 90 → 60 mL, less each day.",
            ),
          ),
        onSite: () =>
          find(
            "tsai",
            tr("看出口紗布", "Checks the exit-site gauze"),
            tr("蔡先生引流出口", "Mr. Tsai’s drain site"),
            tr("紗布乾淨，只有一點點淡紅色。", "Gauze clean, just a faint pink tinge."),
          ),
        onTube: () =>
          find(
            "tsai",
            tr("擠引流管", "Milks the drain tube"),
            tr("蔡先生引流管", "Mr. Tsai’s drain tube"),
            tr("通暢。", "Patent."),
          ),
      }),
    onBack: back,
  });
}

function bedZoom(who: PtId): ZoomCfg {
  if (who === "chen") {
    const v = chenV();
    const shocky = S.shockAt !== null;
    return {
      title: tr("701-2 陳伯伯", "701-2 Mr. Chen"),
      sub: tr("點身體的部位檢查，點臉跟他說話", "Tap a body part to examine; tap his face to talk"),
      draw: (c, now) =>
        drawWardBed(c, now, {
          pt: "chen",
          pale: shocky ? { s: "#d8c0a8", S: "#b8a088" } : v.temp >= 38.5 ? { s: "#ecb494" } : {},
          dawn: true,
          iv: "#f2f6fa",
          iv2: S.abxDrug ? "#f6e7a6" : S.fluidAt !== null ? "#cfe8f4" : null,
          drain: { color: "#93a03a", level: Math.min(1, drainMl() / 500), cloudy: 0.6 },
          dressing: "clean",
          shiver: (S.rigorAt !== null && S.t < S.rigorAt + 4) || shocky,
          o2: !!S.orders.o2,
          npo: S.npoAt !== null,
          water: S.npoAt === null,
          waterNote: "30",
          cup: S.drankAt !== null,
          companion: "chen_dau",
        }),
      hot: () =>
        wardHots({
          ptLabel: tr("陳伯伯（跟他說話）", "Mr. Chen (talk)"),
          companionLabel: tr("雅婷（跟她說話）", "Ya-Ting (talk)"),
          onDressing: chenWound,
          onDrain: () => openDrain("chen"),
          onBelly: () =>
            setZBar(
              [
                C(tr("觸診肚子", "Palpate abdomen"), chenBelly),
                C(tr("聽肺", "Listen to lungs"), chenLung),
                C(tr("量生命徵象", "Take vitals"), () => takeVitals("chen", "chen")),
              ],
              tr("陳伯伯", "Mr. Chen"),
            ),
          onBoard: () =>
            note(
              S.npoAt !== null
                ? tr(
                    "床頭白板：701-2 陳○○ 66y，STG + Roux-en-Y POD4。紅字：NPO。",
                    "Bedside board: 701-2 Chen, 66y, STG + Roux-en-Y POD4. In red: NPO.",
                  )
                : tr(
                    "床頭白板：701-2 陳○○ 66y，STG + Roux-en-Y POD4。專師寫的：「今天開始喝水 30 mL/hr」。",
                    "Bedside board: 701-2 Chen, 66y, STG + Roux-en-Y POD4. In the NP’s writing: “Start water 30 mL/hr today.”",
                  ),
              "chen",
            ),
          onIv: () =>
            note(
              tr(
                `點滴：維持輸液 80 mL/hr${S.fluidAt !== null ? "、LR 500 全速" : ""}${S.abxDrug ? `、${ORD[S.abxDrug]}` : ""}。`,
                `IV: maintenance fluids 80 mL/hr${S.fluidAt !== null ? ", LR 500 wide open" : ""}${S.abxDrug ? `, ${ORD[S.abxDrug]}` : ""}.`,
              ),
              "chen",
            ),
          onTable: () =>
            note(
              S.drankAt !== null
                ? tr("床頭櫃上有一個喝空的水杯。", "There’s a drained water cup on the bedside table.")
                : tr(
                    "床頭櫃上有一個空水杯，和孫子的喜帖。",
                    "On the bedside table: an empty water cup and his grandson’s wedding invitation.",
                  ),
              "chen",
            ),
          tableLabel: tr("床頭櫃", "Bedside table"),
        }),
    };
  }
  if (who === "zhiwei") {
    return {
      title: tr("701-1 志偉", "701-1 Chih-Wei"),
      sub: tr("腹腔鏡疝氣修補（TEP）術後第一天", "POD1 after laparoscopic hernia repair (TEP)"),
      draw: (c, now) =>
        drawWardBed(c, now, {
          pt: "zhiwei",
          dawn: true,
          sitting: true,
          iv: null,
          ports: true,
          water: true,
          cup: true,
        }),
      hot: () =>
        wardHots({
          ptLabel: tr("志偉（跟他說話）", "Chih-Wei (talk)"),
          bellyLabel: tr("傷口與鼠蹊部", "Incisions & groin"),
          onBelly: zhiweiExam,
          onDressing: zhiweiExam,
          onBoard: () =>
            note(
              tr(
                "床頭白板：701-1 林○○ 34y，R't TEP POD1。今日出院。",
                "Bedside board: 701-1 Lin, 34y, R’t TEP POD1. Discharge today.",
              ),
              "zhiwei",
            ),
          onTable: () =>
            note(
              tr(
                "床頭櫃上有吃完的早餐盒、手機充電線，包包已經收好了。",
                "On the bedside table: an empty breakfast box and a phone charger. His bag is already packed.",
              ),
              "zhiwei",
            ),
          tableLabel: tr("床頭櫃", "Bedside table"),
        }),
    };
  }
  return {
    title: tr("702-1 蔡先生", "702-1 Mr. Tsai"),
    sub: tr("肝切除術後第二天", "POD2 after liver resection"),
    draw: (c, now) =>
      drawWardBed(c, now, {
        pt: "tsai",
        sitting: true,
        iv: "#f2f6fa",
        drain: { color: FLUID.serosang, level: 0.12 },
        foley: { color: FLUID.urine, level: 0.45 },
        dressing: "clean",
        water: true,
        cup: true,
      }),
    hot: () =>
      wardHots({
        ptLabel: tr("蔡先生（跟他說話）", "Mr. Tsai (talk)"),
        onDressing: tsaiExam,
        onBelly: () =>
          setZBar(
            [
              C(tr("摸肚子、看傷口", "Belly & wound"), tsaiExam),
              C(tr("量生命徵象", "Take vitals"), () => takeVitals("tsai", "tsai")),
            ],
            tr("蔡先生", "Mr. Tsai"),
          ),
        onDrain: () => openDrain("tsai"),
        onFoley: () => {
          touch("tsai", "tsai");
          find(
            "tsai",
            tr("看尿袋", "Checks the urine bag"),
            tr("蔡先生尿量", "Mr. Tsai’s urine output"),
            tr("淡黃清澈，00:00 到現在約 560 mL。", "Pale yellow and clear, about 560 mL since 00:00."),
          );
        },
        onBoard: () =>
          note(
            tr(
              "床頭白板：702-1 蔡○○ 58y，Hepatectomy（S6/7）POD2。今天拔 Foley、多下床。",
              "Bedside board: 702-1 Tsai, 58y, Hepatectomy (S6/7) POD2. Foley out today, more walking.",
            ),
            "tsai",
          ),
        onIv: () => note(tr("點滴：維持輸液 60 mL/hr。", "IV: maintenance fluids 60 mL/hr."), "tsai"),
        onTable: () =>
          note(
            tr(
              "床頭櫃上有一盒自己帶來的藥：貝樂克（Entecavir）。藥盒上寫著「每天一顆，不能停」。",
              "On the bedside table, a box of medicine he brought from home: Baraclude (entecavir). The box says “One a day. Do not stop.”",
            ),
            "tsai",
          ),
        tableLabel: tr("床頭櫃（自己帶的藥）", "Bedside table (home meds)"),
      }),
  };
}

/* ---------- TPR sheet ---------- */

type TprWho = PtId;
let tprWho: TprWho = "chen";

const TPR_BASE: Record<PtId, Omit<TprCol, "mine">[]> = {
  chen: [
    { hr24: "16", day: 2, temp: 37.5, pulse: 88, sbp: 132, dbp: 80, rr: 18 },
    { hr24: "20", day: 2, temp: 37.3, pulse: 84, sbp: 130, dbp: 78, rr: 18 },
    { hr24: "00", day: 3, temp: 37.2, pulse: 82, sbp: 128, dbp: 76, rr: 18 },
    { hr24: "08", day: 3, temp: 37.2, pulse: 84, sbp: 132, dbp: 78, rr: 18 },
    { hr24: "12", day: 3, temp: 37.3, pulse: 86, sbp: 130, dbp: 80, rr: 18 },
    { hr24: "16", day: 3, temp: 37.4, pulse: 88, sbp: 128, dbp: 76, rr: 18 },
    { hr24: "20", day: 3, temp: 37.6, pulse: 96, sbp: 126, dbp: 74, rr: 20 },
    { hr24: "00", day: 4, temp: 37.8, pulse: 108, sbp: 124, dbp: 72, rr: 22 },
    { hr24: "04", day: 4, temp: 37.9, pulse: 116, sbp: 122, dbp: 70, rr: 24 },
  ],
  tsai: [
    { hr24: "08", day: 1, temp: 37.6, pulse: 92, sbp: 134, dbp: 82, rr: 18 },
    { hr24: "16", day: 1, temp: 37.5, pulse: 88, sbp: 132, dbp: 80, rr: 18 },
    { hr24: "00", day: 2, temp: 37.4, pulse: 84, sbp: 130, dbp: 80, rr: 16 },
    { hr24: "04", day: 2, temp: 37.3, pulse: 80, sbp: 128, dbp: 78, rr: 16 },
  ],
  zhiwei: [
    { hr24: "12", day: 0, temp: 36.8, pulse: 78, sbp: 128, dbp: 78, rr: 16 },
    { hr24: "16", day: 0, temp: 36.9, pulse: 76, sbp: 124, dbp: 76, rr: 16 },
    { hr24: "20", day: 0, temp: 36.8, pulse: 72, sbp: 122, dbp: 74, rr: 14 },
    { hr24: "04", day: 1, temp: 36.6, pulse: 68, sbp: 120, dbp: 72, rr: 14 },
  ],
};

const TPR_SHIFTS: Record<PtId, TprSheet["shifts"]> = {
  chen: [
    { input: 720, urine: 650, drain: 25, drainColor: FLUID.serous },
    { input: 700, urine: 520, drain: 35, drainColor: FLUID.serous },
    { input: 640, urine: 310, drain: 280, drainColor: "#93a03a" },
  ],
  tsai: [
    { input: 820, urine: 700, drain: 90, drainColor: FLUID.serosang },
    { input: 760, urine: 650, drain: 60, drainColor: FLUID.serosang },
    { input: 600, urine: 560, drain: 40, drainColor: FLUID.serous },
  ],
  zhiwei: [
    { input: 900, urine: 450 },
    { input: 400, urine: 600 },
  ],
};

function tprSheet(who: PtId): TprSheet {
  const base = TPR_BASE[who];
  const lastDay = base.at(-1)?.day ?? 0;
  const mine = measured(who).map((x): TprCol => ({
    hr24: clock(x.t).slice(0, 2),
    day: lastDay,
    temp: x.v.temp,
    pulse: x.v.hr,
    sbp: x.v.sbp,
    dbp: x.v.dbp,
    rr: x.v.rr,
    mine: true,
  }));
  const all = [...base, ...mine];
  const dropped = Math.max(0, all.length - 12);
  return {
    cols: all.slice(dropped),
    shifts: TPR_SHIFTS[who],
    mark: who === "chen" && S.seen.has("pulse_chen") && base.length - 1 >= dropped ? base.length - 1 - dropped : null,
  };
}

const TPR_READ: Record<PtId, Record<"temp" | "pulse" | "bp" | "rr" | "io" | "drain", string>> = {
  chen: {
    temp: tr(
      "體溫：POD3 白天 37.2–37.4，晚上 20:00 37.6、00:00 37.8、04:00 37.9。一路往上爬。",
      "Temp: POD3 daytime 37.2–37.4, then 37.6 at 20:00, 37.8 at 00:00, 37.9 at 04:00. Climbing the whole way.",
    ),
    pulse: tr(
      "脈搏：POD3 白天 84–88，20:00 96、00:00 108、04:00 116。半夜開始一路往上。",
      "Pulse: POD3 daytime 84–88, then 96 at 20:00, 108 at 00:00, 116 at 04:00. Climbing steadily since the middle of the night.",
    ),
    bp: tr(
      "血壓：132/78 慢慢掉到 122/70。還在正常範圍，但方向是往下的。",
      "BP: slowly down from 132/78 to 122/70. Still in the normal range, but heading down.",
    ),
    rr: tr("呼吸：18 → 20 → 22 → 24。", "RR: 18 → 20 → 22 → 24."),
    io: tr(
      "I/O：大夜（00–08）輸入 640 mL、尿量只有 310 mL；前兩班都有 500 以上。",
      "I/O: night shift (00–08) intake 640 mL, urine only 310 mL; both earlier shifts were over 500.",
    ),
    drain: tr(
      "引流：POD3 白班 25 mL、小夜 35 mL，都是淡黃清澈；大夜突然 280 mL，色塊是混濁的黃綠色。",
      "Drain: POD3 day shift 25 mL, evening shift 35 mL, both pale yellow and clear; then suddenly 280 mL on the night shift, and the color swatch is cloudy yellow-green.",
    ),
  },
  tsai: {
    temp: tr(
      "體溫：術後第一天 37.6，之後 37.3–37.5，慢慢下降。",
      "Temp: 37.6 on POD1, then 37.3–37.5, slowly coming down.",
    ),
    pulse: tr("脈搏：92 → 80，一路下降。", "Pulse: 92 → 80, trending down."),
    bp: tr("血壓：穩定 128–134 / 78–82。", "BP: stable, 128–134 / 78–82."),
    rr: tr("呼吸：16–18。", "RR: 16–18."),
    io: tr("I/O：每班尿量 560–700 mL，輸入輸出平衡。", "I/O: urine 560–700 mL per shift; intake and output balanced."),
    drain: tr(
      "引流：90 → 60 → 40 mL，淡紅色變淡黃，一天比一天少。",
      "Drain: 90 → 60 → 40 mL, pale red turning pale yellow, less each day.",
    ),
  },
  zhiwei: {
    temp: tr("體溫：36.6–36.9，沒有發燒。", "Temp: 36.6–36.9, no fever."),
    pulse: tr("脈搏：78 → 68。", "Pulse: 78 → 68."),
    bp: tr("血壓：120–128 / 72–78。", "BP: 120–128 / 72–78."),
    rr: tr("呼吸：14–16。", "RR: 14–16."),
    io: tr("尿量：術後自解，兩班共 1,050 mL。", "Urine: voiding on his own since surgery, 1,050 mL over two shifts."),
    drain: tr("沒有引流管。", "No drain."),
  },
};

function readTpr(k: "temp" | "pulse" | "bp" | "rr" | "io" | "drain"): void {
  const who = tprWho;
  if (who === "chen") {
    if (k === "pulse") S.seen.add("pulse_chen");
    if (k === "io") S.seen.add("io_chen");
    if (k === "drain") S.seen.add("drainrow_chen");
  }
  const name = `${PT_NAME[who]} TPR`;
  const txt = TPR_READ[who][k];
  if (!S.findings.some((f) => f.name === name && f.text === txt)) finding(name, txt);
  note(txt, "emr");
}

/** 陳伯伯's CT as a real image (photo mode only; drawn mode keeps the written report). */
function openCt(): void {
  const mark = (name: string, txt: string): void => {
    if (S.findings.some((f) => f.name === name && f.text === txt)) note(txt, "emr");
    else {
      finding(name, txt);
      post("emr", "narr", null, txt);
    }
  };
  openZoom({
    dlg: "emr",
    title: tr("陳伯伯 腹部 CT", "Mr. Chen · CT abdomen"),
    sub: tr("軸切面・十二指腸殘端高度", "Axial · level of the duodenal stump"),
    draw: photoOnly,
    photo: {
      id: "ct_stump",
      overlay: (c, p) => sideMark(c, p),
      hot: [
        {
          x: 50,
          y: 14,
          w: 26,
          h: 26,
          label: tr("右上腹、胰頭前面那團黑黑的東西", "Dark blob in the RUQ, in front of the pancreatic head"),
          fn: () =>
            mark(
              tr("CT 影像", "CT image"),
              tr(
                "十二指腸殘端旁一團積液，裡面有氣泡（黑色的是空氣）：殘端滲漏、腹內膿瘍",
                "Fluid collection beside the duodenal stump with gas bubbles in it (black is air): stump leak with an intra-abdominal abscess",
              ),
            ),
        },
        {
          x: 10,
          y: 28,
          w: 40,
          h: 54,
          label: tr("肝臟", "Liver"),
          fn: () => note(tr("肝臟，沒有明顯異常。", "Liver, nothing obviously abnormal."), "emr"),
        },
        {
          x: 92,
          y: 16,
          w: 64,
          h: 34,
          label: tr("左上腹的腸子", "Bowel in the LUQ"),
          fn: () =>
            note(
              tr(
                "左上腹的腸子含氣，這是正常的腸腔，不是腸外的氣。",
                "The gas in the LUQ bowel is normal: it’s inside the bowel lumen, not air outside the bowel.",
              ),
              "emr",
            ),
        },
        {
          x: 52,
          y: 58,
          w: 20,
          h: 22,
          label: tr("右腎", "Right kidney"),
          fn: () => note(tr("右腎。", "Right kidney."), "emr"),
        },
        {
          x: 100,
          y: 58,
          w: 26,
          h: 22,
          label: tr("左腎", "Left kidney"),
          fn: () => note(tr("左腎。", "Left kidney."), "emr"),
        },
        {
          x: 76,
          y: 62,
          w: 22,
          h: 28,
          label: tr("脊椎與主動脈", "Spine & aorta"),
          fn: () => note(tr("脊椎，前面是主動脈。", "Spine, with the aorta in front of it."), "emr"),
        },
      ],
    },
    hint: tr(
      "片子上的 R 是病人的右邊；腸子外面的黑色氣泡才是問題",
      "The R on the film is the patient’s right; black bubbles outside the bowel are the problem",
    ),
  });
}

function openTpr(who: PtId): void {
  tprWho = who;
  S.seen.add(`tpr_${who}`);
  const tabs = (): ReturnType<typeof C>[] =>
    (["chen", "zhiwei", "tsai"] as const).map((p) => C(PT_NAME[p], () => openTpr(p), p === tprWho));
  openZoom({
    dlg: "emr",
    title: tr(`TPR 單：${PT_NAME[who]}`, `TPR: ${PT_NAME[who]}`),
    sub:
      who === "chen"
        ? tr("POD2 → POD4（今天）", "POD2 → POD4 (today)")
        : who === "tsai"
          ? "POD1 → POD2"
          : tr("手術當天 → POD1", "Day of surgery → POD1"),
    draw: (c, now) => drawTPR(c, now, tprSheet(who)),
    hot: () =>
      tprHots({
        onTemp: () => readTpr("temp"),
        onPulse: () => readTpr("pulse"),
        onBp: () => readTpr("bp"),
        onRr: () => readTpr("rr"),
        onIo: () => readTpr("io"),
        onDrain: () => readTpr("drain"),
      }),
    bar: tabs,
    barLabel: tr("病人", "Patient"),
    hint: tr("點曲線或數字看細節", "Tap a line or number for details"),
  });
}

/* ---------- LLM characters ---------- */

function stateLine(): string {
  const v = chenV();
  const parts = [
    tr(
      `現在 ${clock()}。陳伯伯的實際生命徵象約 ${vtxt(v)}。`,
      `It is ${clock()}. Mr. Chen's actual vitals are about ${vtxt(v)}.`,
    ),
  ];
  if (S.rigorAt !== null && S.t < S.rigorAt + 4)
    parts.push(tr("陳伯伯此刻正在發抖（寒顫）。", "Mr. Chen is shaking right now (rigors)."));
  if (S.drankAt !== null)
    parts.push(
      tr(
        `他 ${clock(S.drankAt)} 開始喝水，喝完肚子更脹更痛${S.vomitAt !== null ? "，還吐了一次" : ""}。`,
        `He started drinking water at ${clock(S.drankAt)}; since then his belly is more bloated and more painful${S.vomitAt !== null ? ", and he has vomited once" : ""}.`,
      ),
    );
  if (S.npoAt !== null) parts.push(tr("醫療團隊已經叫他先禁食。", "The care team has already made him NPO."));
  if (S.shockAt !== null)
    parts.push(
      tr(
        "他現在血壓很低、意識變差，非常虛弱，講話很短。",
        "His blood pressure is now very low and his mental status is worsening; he is very weak and speaks only in short bursts.",
      ),
    );
  return parts.join(tr("", " "));
}

async function chenTalk(text: string): Promise<void> {
  await chat(text, {
    ch: "chen",
    think: "chen",
    speakers: { chen: tr("陳伯伯", "Mr. Chen"), chen_dau: tr("雅婷", "Ya-Ting") },
    body: tr(
      `你在醫學教育遊戲中同時扮演外科病房 701-2 床的兩個人：病人「陳伯伯」與他的女兒「雅婷」。玩家是一般外科見習醫學生（clerk），早上來巡房（pre-round）。
陳伯伯：66 歲退休公車司機，台語腔國語（會講「啊」「歹勢」「免啦」），胃癌，四天前做胃次全切除＋Roux-en-Y 重建。好強、怕麻煩人，一心想趕快吃東西出院，因為下週六孫子結婚。會把不舒服說成「傷口痛啦，沒怎樣」。被直接、具體問到的症狀不會說謊，但會淡化。
雅婷：38 歲，昨晚陪睡。很擔心，但爸爸叫她不要講發抖的事；clerk 問她昨晚怎麼樣、或問到發冷發抖時，她會看爸爸一眼，然後說出來。
【隱藏真相，不能說出】十二指腸殘端滲漏，腹內感染、早期敗血症。
【可揭露的事實】只有被問到相關問題才說，括號是 id：
${(["pain_site", "pain_worse", "rigor", "sob", "bloat", "flatus", "urine", "nosleep", "wedding", "hide"] as const)
  .map((k) => `- (${k}) ${FACTS[k]}`)
  .join("\n")}
rigor：陳伯伯自己被問會說「冷氣太冷啦」，要雅婷講或 clerk 追問才承認。hide 只有雅婷會講。pain_site 要 clerk 問「哪裡痛／痛的位置」或請他指出來才說。
【行動】act 只能是 water、npo、none。clerk 明確告訴他「可以開始喝水」時用 water（雅婷會倒水給他喝）；clerk 明確叫他先不要吃喝、要禁食時用 npo；其他情況 none。
【狀態】${stateLine()}已揭露：${[...S.revealed].join(",") || "無"}`,
      `In this medical education game you play two people at once at bed 701-2 on the surgical ward: the patient "Mr. Chen" and his daughter "Ya-Ting". The player is a general surgery clerk (medical student) doing morning pre-rounds.
Mr. Chen: 66, a retired bus driver who speaks plain, folksy English. He is a Taiwanese speaker at heart: little fillers like "ah", a quick "sorry, sorry" for being a bother, and a wave-off "no need, no need". Gastric cancer; four days ago he had a subtotal gastrectomy with Roux-en-Y reconstruction. Proud and hates troubling anyone; all he wants is to eat soon and go home, because his grandson is getting married next Saturday. He passes off his discomfort as "it's just the wound, it's fine". He won't lie about a symptom when asked about it directly and specifically, but he plays it down.
Ya-Ting: 38, stayed overnight at his bedside. Very worried, but her father told her not to mention the shaking; when the clerk asks her how last night went, or asks about chills or shaking, she glances at her father and then tells.
[HIDDEN TRUTH, never say it] Duodenal stump leak, intra-abdominal infection, early sepsis.
[REVEALABLE FACTS] Only when asked a relevant question; the id is in parentheses:
${(["pain_site", "pain_worse", "rigor", "sob", "bloat", "flatus", "urine", "nosleep", "wedding", "hide"] as const)
  .map((k) => `- (${k}) ${FACTS[k]}`)
  .join("\n")}
rigor: if Mr. Chen himself is asked, he says "the air conditioning's too cold"; he only admits it if Ya-Ting tells or the clerk presses. Only Ya-Ting reveals hide. pain_site is only revealed when the clerk asks "where does it hurt / where exactly" or asks him to point to it.
[ACTION] act can only be water, npo or none. Use water when the clerk explicitly tells him "you can start drinking water" (Ya-Ting will pour him some); use npo when the clerk explicitly tells him not to eat or drink for now / to stay NPO; otherwise none.
[STATE] ${stateLine()} Revealed: ${[...S.revealed].join(",") || "none"}`,
    ),
    extra: `"revealed":[],"act":"none"`,
    after(r) {
      reveal(r.revealed);
      if (r.act === "water" && S.npoAt === null) {
        S.okedWater = true;
        drink(tr("雅婷", "Ya-Ting"));
      } else if (r.act === "npo") {
        S.held = true;
        S.npoAt ??= S.t;
        clearWait("water");
        post("chen", "narr", null, tr("雅婷把水杯收進櫃子裡。", "Ya-Ting puts the water cup away in the cabinet."));
      }
    },
  });
}

async function zhiweiTalk(text: string): Promise<void> {
  await chat(text, {
    ch: "zhiwei",
    think: "zhiwei",
    speakers: { zhiwei: tr("志偉", "Chih-Wei") },
    body: tr(
      `你在醫學教育遊戲中扮演外科病房 701-1 床的「志偉」，34 歲物流公司員工，昨天做右側腹腔鏡疝氣修補（TEP），今天出院。開朗、急著回家，覺得自己「沒事了」。玩家是一般外科見習醫學生（clerk），負責出院衛教。
【可揭露的事實】被問到才說，括號是 id：
${(["z_void", "z_pain", "z_scrotum", "z_move", "z_work"] as const).map((k) => `- (${k}) ${FACTS[k]}`).join("\n")}
z_move 和 z_work：clerk 講到活動、提重物、工作時他才說「啊我明天要幫朋友搬家欸」。
【衛教】clerk 這一次的話裡有清楚交代的衛教項目，把 id 放進 taught：wound 傷口照護與洗澡（防水敷料、可以淋浴、不要泡澡）；activity 活動與提重物（可以走路，幾週內避免提重物、搬家要延後）；pain 止痛藥怎麼吃；warn 要提早回來的警訊（發燒、傷口紅腫流膿、陰囊越來越腫或很痛、尿不出來、劇烈腹痛嘔吐）；opd 回診時間（約一週後門診）。只講一兩個字不算。志偉會針對衛教發問，聽懂會複述。
【狀態】${clock()}。已衛教：${[...S.taught].join(",") || "無"}。已揭露：${[...S.revealed].filter((x) => x.startsWith("z_")).join(",") || "無"}`,
      `In this medical education game you play "Chih-Wei" in bed 701-1 on the surgical ward: 34, works for a logistics company, had a right laparoscopic hernia repair (TEP) yesterday and goes home today. Upbeat, eager to get home, figures he's "fine now". The player is a general surgery clerk (medical student) responsible for his discharge teaching.
[REVEALABLE FACTS] Only when asked; the id is in parentheses:
${(["z_void", "z_pain", "z_scrotum", "z_move", "z_work"] as const).map((k) => `- (${k}) ${FACTS[k]}`).join("\n")}
z_move and z_work: only when the clerk talks about activity, lifting or work does he say "Oh, I'm helping a friend move tomorrow, actually."
[TEACHING] For each teaching item the clerk clearly covers in this message, put its id in taught: wound = wound care and showering (waterproof dressing, showers are fine, no baths or soaking); activity = activity and lifting (walking is fine, avoid heavy lifting for a few weeks, the move has to be postponed); pain = how to take the pain medication; warn = warning signs to come back early (fever, wound redness or pus, scrotum getting more swollen or very painful, unable to pee, severe abdominal pain or vomiting); opd = follow-up (clinic in about a week). A word or two does not count. Chih-Wei asks questions about the teaching and repeats it back once he understands.
[STATE] ${clock()}. Taught: ${[...S.taught].join(",") || "none"}. Revealed: ${[...S.revealed].filter((x) => x.startsWith("z_")).join(",") || "none"}`,
    ),
    extra: `"revealed":[],"taught":[]`,
    after(r) {
      reveal(r.revealed);
      for (const k of strList(r.taught)) if (isTeach(k)) S.taught.add(k);
      if (S.taught.size >= 3 && !S.zhiweiOut) {
        clearWait("zhiwei");
        if (S.taught.size === 3)
          post(
            "zhiwei",
            "narr",
            null,
            tr("志偉把你講的重點打在手機備忘錄裡。", "Chih-Wei types your key points into the notes app on his phone."),
          );
      }
    },
  });
}

async function tsaiTalk(text: string): Promise<void> {
  await chat(text, {
    ch: "tsai",
    think: "tsai",
    speakers: { tsai: tr("蔡先生", "Mr. Tsai") },
    body: tr(
      `你在醫學教育遊戲中扮演外科病房 702-1 床的「蔡先生」，58 歲，B 肝帶原，肝癌，兩天前做右肝部分切除。客氣、很配合，恢復得不錯，對醫療團隊很信任。玩家是一般外科見習醫學生（clerk），來巡房。
【可揭露的事實】被問到才說，括號是 id：
${(["t_walk", "t_pain", "t_eat", "t_hbv"] as const).map((k) => `- (${k}) ${FACTS[k]}`).join("\n")}
t_hbv：clerk 問到平常吃什麼藥、或問有沒有其他問題時，他會說出來。
不要說出任何診斷或醫療建議。
【狀態】${clock()}。${S.etvFixed ? "已經有人告訴他貝樂克會補開。" : ""}已揭露：${[...S.revealed].filter((x) => x.startsWith("t_")).join(",") || "無"}`,
      `In this medical education game you play "Mr. Tsai" in bed 702-1 on the surgical ward: 58, hepatitis B carrier with liver cancer; two days ago he had a partial right hepatectomy. Polite, very cooperative, recovering well, trusts the care team completely. The player is a general surgery clerk (medical student) on rounds.
[REVEALABLE FACTS] Only when asked; the id is in parentheses:
${(["t_walk", "t_pain", "t_eat", "t_hbv"] as const).map((k) => `- (${k}) ${FACTS[k]}`).join("\n")}
t_hbv: he brings it up when the clerk asks what medications he usually takes, or whether he has any other questions.
Do not give any diagnosis or medical advice.
[STATE] ${clock()}. ${S.etvFixed ? "Someone has already told him his Baraclude will be re-ordered. " : ""}Revealed: ${[...S.revealed].filter((x) => x.startsWith("t_")).join(",") || "none"}`,
    ),
    extra: `"revealed":[]`,
    after: (r) => reveal(r.revealed),
  });
}

function orderFromModel(ids: unknown, ch: string, by: "gs_nurse" | "np"): void {
  for (const id of strList(ids))
    if (isOrder(id)) {
      const cur = S.dialog;
      placeOrder(id, ch, by);
      if (S.dialog !== cur) {
        S.dialog = cur;
        renderSide();
      }
    }
}

async function nurseTalk(text: string): Promise<void> {
  await chat(text, {
    ch: "nurse",
    think: "gs_nurse",
    speakers: { gs_nurse: tr("欣怡", "Hsin-Yi") },
    cost: 1,
    body: tr(
      `你在醫學教育遊戲中扮演外科病房大夜班護理師「欣怡」，26 歲，快下班了，累但認真。玩家是一般外科見習醫學生（clerk）。
她夜裡照顧陳伯伯：00:10 他說傷口痛，給 Tramadol；02:30 他說冷、多蓋一條被子；04:00 心跳 116、體溫 37.9，她打電話給值班醫師，醫囑「觀察」；06:00 倒引流 280 mL，覺得比較混濁，記在 I/O 了。她以為是術後疼痛，被 clerk 認真問才會說出「其實他半夜在發抖」「引流的顏色我也覺得怪怪的」。
她可以做：量生命徵象、禁食、抽血、血液培養、引流液送檢、記尿量、給氧。給藥、輸液、CT 需要醫師（阿凱學長或主任）同意${S.rApproved || S.accepted ? "——現在阿凱已經同意了，可以做" : ""}。
【clerk 目前知道與做過的】
${known()}
【可執行的醫囑 id】${(Object.keys(ORD) as OrderId[]).map((k) => `${k}（${ORD[k]}）`).join("、")}
clerk 明確要求的事，把 id 放進 orders。不要編數字、不要說出診斷。`,
      `In this medical education game you play "Hsin-Yi", the night-shift nurse on the surgical ward: 26, nearly off shift, tired but conscientious. The player is a general surgery clerk (medical student).
She looked after Mr. Chen overnight: at 00:10 he said his wound hurt, so she gave tramadol; at 02:30 he said he was cold, so she gave him another blanket; at 04:00 his HR was 116 and temp 37.9, she phoned the on-call doctor, and the order was "observe"; at 06:00 she emptied 280 mL from the drain, thought it looked cloudier, and charted it in the I/O. She assumes it is post-op pain; only when the clerk asks seriously does she say "actually, he was shaking in the middle of the night" and "I thought the drain color looked off, too".
She can: take vitals, make him NPO, draw labs, draw blood cultures, send drain fluid, track urine output, and give oxygen. Medications, IV fluids and CT need a doctor's approval (Kai or Dr. Chiang)${S.rApproved || S.accepted ? " - Kai has now approved them, so she can do them" : ""}.
[WHAT THE CLERK KNOWS AND HAS DONE SO FAR]
${known()}
[ORDER IDS YOU CAN CARRY OUT] ${(Object.keys(ORD) as OrderId[]).map((k) => `${k} (${ORD[k]})`).join(", ")}
For anything the clerk explicitly asks for, put the id in orders. Do not make up numbers and do not state the diagnosis.`,
    ),
    extra: `"orders":[]`,
    after: (r) => orderFromModel(r.orders, "nurse", "gs_nurse"),
  });
}

async function npTalk(text: string): Promise<void> {
  await chat(text, {
    ch: "np",
    think: "np",
    speakers: { np: tr("佩珊學姊", "Pei-Shan") },
    cost: 1,
    body: tr(
      `你在醫學教育遊戲中扮演外科專科護理師「佩珊學姊」，35 歲，十年經驗，病房真正的運作者。講話快、很有效率、很相信臨床路徑（「路徑就是這樣走的」），會教 clerk 也會擋 clerk（「主任沒說的不要亂開」）。叫 clerk「學弟」。
她今天的計畫：志偉出院（她要 clerk 做衛教）、陳伯伯照胃切除路徑 POD4 開始喝水 30 mL/hr、蔡先生拔 Foley。
如果 clerk 拿出具體證據（心跳趨勢、發燒發抖、引流液變色變多、右上腹 guarding、白血球）說陳伯伯不對勁，她會停下來，承認路徑上寫「出現發燒、心跳 >100、引流性狀改變時暫停並通知醫師」，同意先禁食、幫忙抽血送檢，但用藥、輸液、CT 要 clerk 去說服阿凱或報告主任${S.rApproved || S.accepted ? "（現在阿凱已經同意，她會照開）" : ""}。clerk 只說「我覺得怪怪的」而沒有證據，她會說「哪裡怪？數字給我看」。
clerk 提到蔡先生的貝樂克（Entecavir）沒有續開，她會說「欸真的耶，我請阿凱補開」。
【clerk 目前知道與做過的】
${known()}
【action】water：clerk 同意讓陳伯伯照路徑開始喝水；hold：clerk 要先暫停喝水或禁食；discharge：clerk 說志偉衛教好了可以辦出院；etv：clerk 指出蔡先生的 Entecavir 要續用；none：其他。
【可執行的醫囑 id】${(Object.keys(ORD) as OrderId[]).map((k) => `${k}（${ORD[k]}）`).join("、")}
clerk 明確要求的事，把 id 放進 orders。不要編數字、不要說出診斷。`,
      `In this medical education game you play "Pei-Shan", the surgical nurse practitioner (NP): 35, ten years' experience, the person who actually runs the ward. Talks fast, very efficient, a firm believer in clinical pathways ("that's how the pathway goes"); she teaches the clerk but also reins the clerk in ("if the attending didn't say so, don't go ordering things"). She treats the clerk as her junior, like a brisk older sister.
Her plan for today: Chih-Wei goes home (she wants the clerk to do the discharge teaching), Mr. Chen starts water at 30 mL/hr on POD4 per the gastrectomy pathway, Mr. Tsai's Foley comes out.
If the clerk brings concrete evidence (heart-rate trend, fever and rigors, drain fluid changing color and increasing, RUQ guarding, white count) that Mr. Chen isn't right, she stops and admits the pathway says "hold the pathway and notify the physician for fever, HR >100, or a change in drain character"; she agrees to make him NPO first and helps draw labs and send samples, but for medications, IV fluids and CT the clerk has to convince Kai or report to Dr. Chiang${S.rApproved || S.accepted ? " (Kai has now approved, so she will carry them out)" : ""}. If the clerk only says "something feels off" with no evidence, she says "Off how? Show me numbers."
If the clerk mentions that Mr. Tsai's Baraclude (entecavir) wasn't re-ordered, she says "Oh, you're right. I'll get Kai to order it."
[WHAT THE CLERK KNOWS AND HAS DONE SO FAR]
${known()}
[ACTION] water: the clerk agrees to let Mr. Chen start water per the pathway; hold: the clerk wants to hold the water or make him NPO; discharge: the clerk says Chih-Wei's teaching is done and he can be discharged; etv: the clerk points out that Mr. Tsai's entecavir needs to be continued; none: anything else.
[ORDER IDS YOU CAN CARRY OUT] ${(Object.keys(ORD) as OrderId[]).map((k) => `${k} (${ORD[k]})`).join(", ")}
For anything the clerk explicitly asks for, put the id in orders. Do not make up numbers and do not state the diagnosis.`,
    ),
    extra: `"orders":[],"action":"none"`,
    after(r) {
      if (r.action === "water" && S.npoAt === null) {
        S.okedWater = true;
        post(
          "np",
          "npc",
          "np",
          tr("好，那我去跟陳伯伯講可以開始喝水了。", "Okay, I’ll go tell Mr. Chen he can start drinking water."),
        );
        const alive = sessionGuard();
        setTimeout(() => {
          if (alive()) drink(tr("佩珊學姊叫雅婷", "At Pei-Shan’s word, Ya-Ting"));
        }, 800);
      } else if (r.action === "hold") {
        S.held = true;
        clearWait("water");
      } else if (r.action === "discharge") discharge("clerk");
      else if (r.action === "etv" && !S.etvFixed) {
        S.etvFixed = true;
        finding(
          tr("用藥整合", "Med reconciliation"),
          tr(
            "蔡先生住院後 Entecavir 沒有續開，已請專師補開",
            "Mr. Tsai’s entecavir wasn’t continued on admission; asked the NP to re-order it",
          ),
        );
      }
      orderFromModel(r.orders, "np", "np");
    },
  });
}

async function rTalk(text: string): Promise<void> {
  const ev = evidence();
  await chat(text, {
    ch: "r",
    think: "gs_r",
    speakers: { gs_r: tr("阿凱", "Kai") },
    body: tr(
      `你在醫學教育遊戲中扮演一般外科第三年住院醫師「阿凱」，昨天值班到兩點，很累、講話很快，手機一直響。人不壞，但習慣用流程替代判斷（「pathway 說今天喝水就喝水」「POD4 有點燒很正常啦」）。叫 clerk「學弟」，中英夾雜。
${S.vsGone ? "他現在在刀房刷手準備 Whipple，接電話很急。" : S.conf === "open" || S.conf === "done" ? "他在會議室。" : "他在護理站趕病歷，七點半要晨會。"}
【真相，不要直接說出】陳伯伯十二指腸殘端滲漏、早期敗血症。
【clerk 手上的證據】${ev.join("；") || "沒有"}
如果 clerk 講出至少兩個具體證據並明確說懷疑滲漏或腹內感染，他會愣一下，翻 TPR，說「靠，真的」，同意 workup：action 用 approve，交代 NPO、抽血加 lactate、culture 兩套、引流液送 bili/amylase、LR 500、抗生素等 culture 抽完就上 Tazocin、他去排 CT，並叫 clerk 晨會自己跟主任報告。證據不夠時他會說「POD4 有點燒很正常」，action 用 none。
【clerk 目前知道與做過的】
${known()}`,
      `In this medical education game you play "Kai", a third-year general surgery resident: on call until 2 a.m. last night, exhausted, talks fast, phone keeps buzzing. Not a bad guy, but in the habit of letting protocol stand in for judgment ("pathway says water today, so it's water today", "a bit of fever on POD4 is totally normal"). He treats the clerk as his junior and talks in clipped surgical shorthand.
${S.vsGone ? "He is in the OR scrubbing for the Whipple, rushed on the phone." : S.conf === "open" || S.conf === "done" ? "He is in the conference room." : "He is at the nurses' station catching up on notes; morning conference is at 7:30."}
[TRUTH, don't say it outright] Mr. Chen has a duodenal stump leak and early sepsis.
[EVIDENCE THE CLERK HAS] ${ev.join("; ") || "none"}
If the clerk states at least two concrete pieces of evidence and clearly says they suspect a leak or an intra-abdominal infection, he stops short, flips through the TPR, says "Damn, you're right," and agrees to the workup: use action approve; he lays out NPO, labs plus lactate, two sets of cultures, drain fluid for bili/amylase, LR 500, antibiotics (Tazocin as soon as the cultures are drawn), says he'll book the CT, and tells the clerk to present it to Dr. Chiang themselves at conference. If the evidence isn't enough he says "a bit of fever on POD4 is normal," and action is none.
[WHAT THE CLERK KNOWS AND HAS DONE SO FAR]
${known()}`,
    ),
    extra: `"action":"none"`,
    after(r) {
      if (r.action === "approve" && !S.rApproved) {
        if (ev.length < 2) {
          post("r", "npc", "gs_r", tr("你給我數字啦，不要只說感覺。", "Give me numbers, not feelings."));
          return;
        }
        S.rApproved = true;
        S.rApprovedAt = S.t;
        S.npoAt ??= S.t;
        S.held = true;
        clearWait("water");
        S.orders.ct ??= { at: S.t, ready: S.t + 30, done: false };
        post(
          "r",
          "narr",
          null,
          tr(
            "阿凱在電腦上把醫囑打了進去。用藥、輸液和 CT 現在可以請欣怡或佩珊學姊執行了。",
            "Kai types the orders into the computer. Hsin-Yi or Pei-Shan can now carry out meds, IV fluids and CT.",
          ),
        );
      }
    },
  });
}

async function vsTalk(text: string): Promise<void> {
  const ev = evidence();
  const where = S.vsGone
    ? tr(
        "他在刀房，正在 Whipple，流動護理師拿著電話放在他耳邊（擴音）。",
        "He is in the OR in the middle of the Whipple; the circulating nurse is holding the phone to his ear (on speaker).",
      )
    : S.vsHere
      ? tr(
          "他在會議室主持晨會，旁邊是阿凱。",
          "He is in the conference room running morning conference, with Kai beside him.",
        )
      : tr(
          "他在開車來醫院的路上，用車上的藍牙接電話。",
          "He is driving to the hospital and takes the call over the car’s Bluetooth.",
        );
  const inConf = S.conf === "open";
  if (inConf) S.confTalks++;
  await chat(text, {
    ch: "vs",
    think: "gs_vs",
    tier: "default",
    speakers: inConf
      ? { gs_vs: tr("江主任", "Dr. Chiang"), gs_r: tr("阿凱", "Kai") }
      : { gs_vs: tr("江主任", "Dr. Chiang") },
    body: tr(
      `你在醫學教育遊戲中扮演一般外科主治「江主任」（肝膽胰），55 歲，話少、極度重視效率。${where}
他要的 presentation：第一句就講最要緊的事（哪一床、什麼問題、多急），再給支持的數據，最後講計畫。clerk 從年齡、病史開始講流水帳時，他會打斷：「重點？」「所以呢？」。學生講得好，他只會說「嗯」，然後問下一個關鍵問題。不直接說出診斷。中英夾雜（「POD 幾？」「Drain 什麼顏色？」「Culture 抽了沒？」）。${inConf ? "阿凱在旁邊，只有在被問到或 clerk 講到路徑時才插話（「路徑今天是喝水」）。" : ""}
【真相】陳伯伯，66 歲，胃次全切除＋Roux-en-Y POD4：十二指腸殘端滲漏，腹內感染，早期敗血症。目前約 ${vtxt(chenV())}。合理計畫：NPO、抽血（含 lactate、culture）、引流液 bili/amylase、輸液、廣效抗生素、CT 看積液，視情況經皮引流或回刀房。另外兩床：志偉 TEP POD1 出院；蔡先生肝切除 POD2 進展順利（Entecavir 要續用）。
【clerk 手上的證據】${ev.join("；") || "沒有"}
【clerk 目前知道與做過的】（講出以外的數據就追問「哪來的？」）
${known()}
${inConf && S.confTalks === 1 ? "【headline】這是 clerk 晨會的第一段報告。lead：第一句就點出陳伯伯懷疑滲漏／腹內感染／敗血症（或同義的嚴重問題）；buried：有講到但不是開頭；none：沒講到。" : "【headline】填 none。"}
【action】accept_plan：clerk 明確說懷疑陳伯伯滲漏或腹內感染，並給出至少兩個具體證據。你同意，簡短下指令（NPO、Tazocin、culture、CT、準備引流），給一句評語。
dismiss：clerk 沒有看過病人或講不出數據。continue：其他情況。`,
      `In this medical education game you play "Dr. Chiang", a general surgery attending (HPB): 55, a man of few words who prizes efficiency above all. ${where}
The presentation he wants: the most important thing in the first sentence (which bed, what problem, how urgent), then the supporting data, then the plan. When the clerk launches into a rambling chronological account starting with age and history, he cuts in: "Point?" "So?" When a student presents well he only says "Mm," then asks the next key question. He never states the diagnosis outright. He talks in clipped surgical shorthand ("POD what?" "Drain color?" "Cultures drawn?").${inConf ? " Kai is beside him and only cuts in when asked or when the clerk brings up the pathway ('pathway says water today')." : ""}
[TRUTH] Mr. Chen, 66, POD4 after subtotal gastrectomy + Roux-en-Y: duodenal stump leak, intra-abdominal infection, early sepsis. Currently about ${vtxt(chenV())}. Reasonable plan: NPO, labs (including lactate and cultures), drain fluid bili/amylase, IV fluids, broad-spectrum antibiotics, CT for a collection, then percutaneous drainage or back to the OR as needed. The other two beds: Chih-Wei, TEP POD1, going home; Mr. Tsai, hepatectomy POD2, progressing well (entecavir needs to be continued).
[EVIDENCE THE CLERK HAS] ${ev.join("; ") || "none"}
[WHAT THE CLERK KNOWS AND HAS DONE SO FAR] (if they cite data beyond this, press them: "Where's that from?")
${known()}
${inConf && S.confTalks === 1 ? "[headline] This is the first part of the clerk's conference presentation. lead: the very first sentence flags Mr. Chen as a suspected leak / intra-abdominal infection / sepsis (or an equally serious problem); buried: mentioned, but not up front; none: not mentioned." : "[headline] Set none."}
[ACTION] accept_plan: the clerk clearly says they suspect Mr. Chen has a leak or intra-abdominal infection and gives at least two concrete pieces of evidence. You agree, give brief orders (NPO, Tazocin, cultures, CT, prepare for drainage) and one line of feedback.
dismiss: the clerk hasn't seen the patient or can't give data. continue: anything else.`,
    ),
    extra: `"headline":"none","action":"continue"`,
    after(r) {
      if (inConf && S.headline === null && isHeadline(r.headline)) S.headline = r.headline;
      if (r.action === "accept_plan" && !S.accepted) {
        if (ev.length < 2) {
          post("vs", "npc", "gs_vs", tr("你的根據呢？給我數字。", "Based on what? Give me numbers."));
          S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
          return;
        }
        accept(S.vsGone || !S.vsHere ? "phone" : "conf");
      } else if (r.action === "dismiss") {
        S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
      }
    },
  });
}

/* ---------- discharge ---------- */

function discharge(by: "clerk" | "np"): void {
  if (S.zhiweiOut) return;
  if (by === "clerk" && S.taught.size < 3) {
    post(
      "np",
      "npc",
      "np",
      tr(
        "衛教講完了嗎？他知道多久不能提重物嗎？再去講清楚一點。",
        "Is the teaching done? Does he know how long he can’t lift anything heavy? Go back and make it clearer.",
      ),
    );
    return;
  }
  S.zhiweiOut = true;
  S.dischargeBy = by;
  clearWait("zhiwei");
  if (by === "np")
    post(
      "np",
      "npc",
      "np",
      tr(
        "志偉的衛教我順便講掉了喔，你忙陳伯伯吧。",
        "I went ahead and did Chih-Wei’s teaching. You focus on Mr. Chen.",
      ),
    );
  else post("np", "npc", "np", tr("OK，志偉出院單我來印。", "OK, I’ll print Chih-Wei’s discharge papers."));
  const z = mkEnt({ k: "zhiwei", id: "zhiwei", x: 2, y: 3, act: "zhiwei" });
  S.ents.push(z);
  const alive = sessionGuard();
  setTimeout(() => {
    if (!alive()) return;
    post("zhiwei", "npc", "zhiwei", tr("謝謝醫生！我會乖乖的啦～", "Thanks, Doc! I’ll behave, promise!"));
    leaveBy(z, DOOR);
  }, 600);
}

/* ---------- dialogs ---------- */

const bedOpen = (who: PtId) => (): void => openZoom({ dlg: who, ...bedZoom(who) });

const dialogs: Level["dialogs"] = {
  chen: {
    title: tr("701-2 陳伯伯", "701-2 Mr. Chen"),
    sub: () =>
      S.shockAt !== null
        ? tr("叫他反應變慢", "Slow to respond")
        : S.rigorAt !== null && S.t < S.rigorAt + 4
          ? tr("正在發抖", "Shaking")
          : tr("胃次全切除 POD4，女兒雅婷陪著", "Subtotal gastrectomy POD4, daughter Ya-Ting with him"),
    pics: ["chen", "chen_dau"],
    ch: "chen",
    talk: true,
    ph: tr("對陳伯伯或雅婷說…", "Say something to Mr. Chen or Ya-Ting…"),
    choices() {
      return [
        C(tr("床邊特寫", "Bedside view"), bedOpen("chen")),
        C(tr("量生命徵象", "Take vitals"), () => takeVitals("chen", "chen")),
        C(tr("觸診肚子", "Palpate abdomen"), chenBelly),
        C(tr("看傷口", "Check wound"), chenWound),
        C(tr("看引流袋", "Check drain"), () => openDrain("chen")),
        C(tr("聽肺", "Listen to lungs"), chenLung),
      ];
    },
    onTalk: chenTalk,
  },
  zhiwei: {
    title: tr("701-1 志偉", "701-1 Chih-Wei"),
    sub: () => (S.zhiweiOut ? tr("出院了", "Discharged") : tr("TEP POD1，今天出院", "TEP POD1, going home today")),
    pics: ["zhiwei"],
    ch: "zhiwei",
    talk: () => !S.zhiweiOut,
    ph: tr("問志偉、或幫他做出院衛教…", "Ask Chih-Wei, or do his discharge teaching…"),
    choices() {
      if (S.zhiweiOut) return [];
      return [
        C(tr("床邊特寫", "Bedside view"), bedOpen("zhiwei")),
        C(tr("量生命徵象", "Take vitals"), () => takeVitals("zhiwei", "zhiwei")),
        C(tr("看傷口與鼠蹊部", "Check incisions & groin"), zhiweiExam),
      ];
    },
    onTalk: zhiweiTalk,
  },
  tsai: {
    title: tr("702-1 蔡先生", "702-1 Mr. Tsai"),
    sub: () =>
      S.tsaiInBed
        ? tr("肝切除 POD2", "Hepatectomy POD2")
        : tr("推著點滴架在走廊散步", "Walking the hall with his IV pole"),
    pics: ["tsai"],
    ch: "tsai",
    talk: true,
    ph: tr("問蔡先生…", "Ask Mr. Tsai…"),
    choices() {
      return [
        C(tr("床邊特寫", "Bedside view"), bedOpen("tsai"), !S.tsaiInBed),
        C(tr("量生命徵象", "Take vitals"), () => takeVitals("tsai", "tsai")),
        C(tr("摸肚子、看傷口", "Belly & wound"), tsaiExam, !S.tsaiInBed),
        C(tr("看引流袋", "Check drain"), () => openDrain("tsai"), !S.tsaiInBed),
      ];
    },
    onTalk: tsaiTalk,
  },
  nurse: {
    title: tr("欣怡", "Hsin-Yi"),
    sub: tr("大夜班護理師，08:00 下班", "Night-shift nurse, off at 08:00"),
    pics: ["gs_nurse"],
    ch: "nurse",
    talk: true,
    ph: tr("問欣怡、或請她做事…", "Ask Hsin-Yi, or ask her to do something…"),
    choices() {
      const o = (id: OrderId): ReturnType<typeof C> =>
        C(ORD[id], () => placeOrder(id, "nurse", "gs_nurse"), !!S.orders[id] && id !== "vitals");
      return [
        C(
          tr("聽交班", "Get handoff"),
          () => {
            S.handover = true;
            clearWait("handover");
            me("nurse", tr("（聽交班）", "(Listens to handoff)"));
            post(
              "nurse",
              "npc",
              "gs_nurse",
              tr(
                "701-1 志偉，TEP POD1，晚上有自解小便，今天出院。701-2 陳伯伯 POD4，半夜說傷口痛，00:10 給一次 Tramadol；04:00 心跳 116、體溫 37.9，我有打給值班醫師，說觀察。06:00 引流倒了 280，比較混濁，記在 I/O。702-1 蔡先生 POD2 很穩，一早就在走廊散步了。",
                "701-1 Chih-Wei, TEP POD1, voided on his own overnight, going home today. 701-2 Mr. Chen, POD4, said his wound hurt in the night, got one dose of tramadol at 00:10; at 04:00 HR 116, temp 37.9. I called the on-call doctor, who said to observe. At 06:00 I emptied 280 from the drain, a bit cloudier; it’s in the I/O. 702-1 Mr. Tsai, POD2, very stable, been walking the hall since early this morning.",
              ),
            );
            spend(2);
          },
          S.handover,
        ),
        tr("檢查", "Tests"),
        o("vitals"),
        o("labs"),
        o("bcx"),
        o("drainlab"),
        tr("處置", "Care"),
        o("npo"),
        o("uo"),
        o("o2"),
        tr("需要醫師同意", "Needs MD approval"),
        o("ivf"),
        o("tazo"),
        o("cromtz"),
        o("cef"),
        o("ct"),
      ];
    },
    onTalk: nurseTalk,
  },
  np: {
    title: tr("佩珊學姊", "Pei-Shan"),
    sub: tr("外科專科護理師", "Surgical NP"),
    pics: ["np"],
    ch: "np",
    talk: true,
    ph: tr("跟佩珊學姊討論…", "Talk it over with Pei-Shan…"),
    choices() {
      return [
        C(tr("志偉衛教好了，辦出院", "Chih-Wei’s teaching done: discharge"), () => discharge("clerk"), S.zhiweiOut),
        C(tr("陳伯伯先不要喝水", "Hold Mr. Chen’s water"), () => {
          me("np", tr("陳伯伯先不要喝水。", "Let’s hold off on water for Mr. Chen."));
          if (evidence().length >= 1) {
            S.held = true;
            clearWait("water");
            post(
              "np",
              "npc",
              "np",
              tr(
                "……為什麼？你給我理由。好吧，先 hold，你要跟阿凱講。",
                "…Why? Give me a reason. Fine, we’ll hold it, but you tell Kai.",
              ),
            );
          } else
            post(
              "np",
              "npc",
              "np",
              tr("路徑今天就是喝水欸。你有什麼理由？", "The pathway says water today. What’s your reason?"),
            );
          spend(1);
        }),
      ];
    },
    onTalk: npTalk,
  },
  r: {
    title: tr("阿凱（R3）", "Kai (R3)"),
    sub: () => (S.vsGone ? tr("電話（刀房）", "Phone (OR)") : tr("外科住院醫師", "Surgery resident")),
    pics: ["gs_r"],
    ch: "r",
    talk: () => !S.accepted,
    ph: tr("跟阿凱學長報告…", "Present to Kai…"),
    onTalk: rTalk,
  },
  vs: {
    title: tr("江主任", "Dr. Chiang"),
    sub: () =>
      S.conf === "open"
        ? tr("晨會中", "In conference")
        : S.vsGone
          ? tr("電話（刀房擴音）", "Phone (OR, on speaker)")
          : S.vsHere
            ? tr("會議室", "Conference room")
            : tr("電話（開車中）", "Phone (driving)"),
    pics: () => (S.conf === "open" ? ["gs_vs", "gs_r"] : ["gs_vs"]),
    ch: "vs",
    talk: () => !S.accepted,
    ph: () =>
      S.conf === "open"
        ? tr("晨會報告：第一句講最要緊的事", "Present: lead with what matters most")
        : tr("向江主任報告…", "Present to Dr. Chiang…"),
    keepOpen: true,
    onOpen() {
      if (S.vsHere && !S.vsGone && S.conf === "ready" && zone(P.x, P.y) === "c") openConf();
      return undefined;
    },
    onTalk: vsTalk,
  },
  emr: {
    title: tr("護理站電腦", "Station computer"),
    sub: tr("TPR、檢驗、用藥、手術與護理紀錄", "TPR, labs, meds, op and nursing notes"),
    ch: "emr",
    choices() {
      return [
        tr("TPR 單", "TPR sheets"),
        C(tr("陳伯伯", "Mr. Chen"), () => openTpr("chen")),
        C(tr("志偉", "Chih-Wei"), () => openTpr("zhiwei")),
        C(tr("蔡先生", "Mr. Tsai"), () => openTpr("tsai")),
        tr("病歷", "Chart"),
        C(tr("檢驗結果", "Lab results"), () => openDialog("labs")),
        C(tr("用藥", "Medications"), () => openDialog("meds")),
        C(tr("手術紀錄", "Op notes"), () => openDialog("opnote")),
        C(tr("夜班護理紀錄", "Night nursing notes"), () => openDialog("nursenote")),
        ...(S.orders.ct?.done && getImagery() === "photo"
          ? [tr("影像", "Imaging"), C(tr("陳伯伯 CT", "Mr. Chen’s CT"), openCt)]
          : []),
      ];
    },
  },
  labs: {
    title: tr("檢驗結果", "Lab results"),
    sub: tr("陳伯伯／蔡先生", "Mr. Chen / Mr. Tsai"),
    ch: "labs",
    doc: doc(() => {
      S.seen.add("cbc_chen");
      const row = (id: OrderId, pending: string, done: string): string => {
        const o = S.orders[id];
        return o
          ? o.done
            ? done
            : tr(`${pending}（約 ${clock(o.ready)}）`, `${pending} (about ${clock(o.ready)})`)
          : tr("沒開", "Not ordered");
      };
      return [
        [
          tr("陳伯伯 POD3 05:30", "Mr. Chen POD3 05:30"),
          tr(
            "WBC 9,800、Hb 11.0、PLT 248k；CRP 7.8；Cr 0.9；T-bil 0.9",
            "WBC 9,800, Hb 11.0, PLT 248k; CRP 7.8; Cr 0.9; T-bil 0.9",
          ),
        ],
        [
          tr("陳伯伯 今天 05:30（例行）", "Mr. Chen today 05:30 (routine)"),
          tr(
            "WBC 17,900（seg 88%、band 7%）、Hb 11.4、PLT 196k。生化處理中，約 07:40 出來。",
            "WBC 17,900 (segs 88%, bands 7%), Hb 11.4, PLT 196k. Chemistry pending, out around 07:40.",
          ),
        ],
        [
          tr("陳伯伯 例行生化", "Mr. Chen routine chem"),
          S.t >= 40
            ? tr(
                "CRP 21.6 mg/dL；BUN 32、Cr 1.5；T-bil 1.6；Albumin 2.8",
                "CRP 21.6 mg/dL; BUN 32, Cr 1.5; T-bil 1.6; Albumin 2.8",
              )
            : tr("處理中，約 07:40", "Pending, around 07:40"),
        ],
        [
          tr("陳伯伯 加驗", "Mr. Chen add-on labs"),
          row(
            "labs",
            tr("急件處理中", "Stat, pending"),
            tr(
              `lactate ${S.t >= 45 ? "4.2" : "3.6"} mmol/L；CRP 21.6；Cr 1.5`,
              `lactate ${S.t >= 45 ? "4.2" : "3.6"} mmol/L; CRP 21.6; Cr 1.5`,
            ),
          ),
        ],
        [
          tr("陳伯伯 引流液", "Mr. Chen drain fluid"),
          row(
            "drainlab",
            tr("處理中", "Pending"),
            tr(
              "Bilirubin 9.8 mg/dL（血清 1.6）；Amylase 2,150 U/L",
              "Bilirubin 9.8 mg/dL (serum 1.6); Amylase 2,150 U/L",
            ),
          ),
        ],
        [
          tr("陳伯伯 血液培養", "Mr. Chen blood cultures"),
          S.orders.bcx ? tr("已送，要兩三天", "Sent; takes 2–3 days") : tr("沒抽", "Not drawn"),
        ],
        [
          tr("陳伯伯 腹部 CT", "Mr. Chen · CT abdomen"),
          S.orders.ct
            ? S.orders.ct.done
              ? tr(
                  "十二指腸殘端旁 6 cm 積液含氣泡，周圍脂肪浸潤；右側少量肋膜積液。胃空腸吻合口未見明顯滲漏。",
                  "6 cm fluid collection with gas bubbles beside the duodenal stump, surrounding fat stranding; small right pleural effusion. No obvious leak at the gastrojejunal anastomosis.",
                )
              : tr(
                  `排程中（約 ${clock(S.orders.ct.ready)} 有初步報告）`,
                  `Scheduled (preliminary report around ${clock(S.orders.ct.ready)})`,
                )
            : tr("沒排", "Not ordered"),
        ],
        [
          tr("蔡先生 POD2 05:30", "Mr. Tsai POD2 05:30"),
          tr(
            "WBC 10,200、Hb 11.8、PLT 152k；AST/ALT 186/164（POD1 320/290）；T-bil 1.3；INR 1.2；Cr 0.8",
            "WBC 10,200, Hb 11.8, PLT 152k; AST/ALT 186/164 (POD1 320/290); T-bil 1.3; INR 1.2; Cr 0.8",
          ),
        ],
      ];
    }),
  },
  meds: {
    title: tr("用藥", "Medications"),
    sub: tr("目前醫囑與住院前用藥", "Current orders and home meds"),
    ch: "meds",
    doc: doc(() => {
      S.seen.add("meds_tsai");
      return [
        [
          tr("陳伯伯 目前", "Mr. Chen, current"),
          tr(
            "維持輸液 80 mL/hr；Pantoprazole 40 mg IV qd；Tramadol 50 mg IV prn；Cefazolin 已於 POD1 停用",
            "Maintenance IV fluids 80 mL/hr; Pantoprazole 40 mg IV qd; Tramadol 50 mg IV prn; Cefazolin stopped on POD1",
          ),
        ],
        [tr("陳伯伯 住院前", "Mr. Chen, home"), "Amlodipine 5 mg qd"],
        [tr("志偉 目前", "Chih-Wei, current"), "Acetaminophen 500 mg q6h prn"],
        [
          tr("蔡先生 目前", "Mr. Tsai, current"),
          tr(
            "維持輸液 60 mL/hr；Amlodipine 5 mg qd；Pantoprazole 40 mg IV qd；Acetaminophen prn",
            "Maintenance IV fluids 60 mL/hr; Amlodipine 5 mg qd; Pantoprazole 40 mg IV qd; Acetaminophen prn",
          ),
        ],
        [
          tr("蔡先生 住院前", "Mr. Tsai, home"),
          tr(
            `Entecavir（貝樂克）0.5 mg qd；Amlodipine 5 mg qd${S.etvFixed ? "　→ Entecavir 已補開" : "　（Entecavir 住院後沒有續開）"}`,
            `Entecavir (Baraclude) 0.5 mg qd; Amlodipine 5 mg qd${S.etvFixed ? " → entecavir re-ordered" : " (entecavir not continued on admission)"}`,
          ),
        ],
      ];
    }),
  },
  opnote: {
    title: tr("手術紀錄", "Op notes"),
    sub: tr("三床", "All three beds"),
    ch: "opnote",
    doc: doc(() => {
      S.seen.add("op_chen");
      return [
        [
          tr("陳伯伯（4 天前）", "Mr. Chen (4 days ago)"),
          tr(
            "胃體下部腺癌。Subtotal gastrectomy、D2 lymph node dissection、Roux-en-Y reconstruction。十二指腸殘端以 stapler 關閉並加強縫合。右側肝下、靠近十二指腸殘端放一條 closed suction drain（黃色標籤）。EBL 200 mL。",
            "Adenocarcinoma of the lower gastric body. Subtotal gastrectomy, D2 lymph node dissection, Roux-en-Y reconstruction. Duodenal stump closed with a stapler and oversewn. One closed suction drain placed right subhepatic, near the duodenal stump (yellow tag). EBL 200 mL.",
          ),
        ],
        [
          tr("蔡先生（2 天前）", "Mr. Tsai (2 days ago)"),
          tr(
            "HCC 4 cm，S6/7。Right posterior sectionectomy。Pringle maneuver 2 次。右側橫膈下 drain 一條。EBL 400 mL。",
            "HCC 4 cm, S6/7. Right posterior sectionectomy. Pringle maneuver x2. One drain, right subphrenic. EBL 400 mL.",
          ),
        ],
        [
          tr("志偉（昨天）", "Chih-Wei (yesterday)"),
          tr(
            "Right indirect inguinal hernia。Laparoscopic TEP，mesh 15×15 cm。過程順利。",
            "Right indirect inguinal hernia. Laparoscopic TEP, mesh 15×15 cm. Uneventful.",
          ),
        ],
      ];
    }),
  },
  nursenote: {
    title: tr("夜班護理紀錄", "Night nursing notes"),
    sub: tr("大夜班　欣怡", "Night shift · Hsin-Yi"),
    ch: "nursenote",
    doc: doc(() => {
      S.seen.add("note_chen");
      return [
        [
          "701-2 00:10",
          tr(
            "主訴傷口疼痛 VAS 6，依醫囑給予 Tramadol 50 mg IV。",
            "C/o wound pain, VAS 6. Tramadol 50 mg IV given per order.",
          ),
        ],
        [
          "701-2 02:30",
          tr(
            "病人訴冷，蓋兩條被子仍覺冷，予加被保暖。家屬陪伴。",
            "Pt c/o feeling cold, still cold under two blankets; extra blanket given. Family at bedside.",
          ),
        ],
        [
          "701-2 04:00",
          tr(
            "BT 37.9°C、HR 116、BP 122/70、RR 24。電話告知值班醫師，醫囑：續觀察。",
            "BT 37.9°C, HR 116, BP 122/70, RR 24. On-call doctor notified by phone; order: continue to observe.",
          ),
        ],
        [
          "701-2 06:00",
          tr(
            "倒引流液 280 mL，性狀較混濁、黃綠色。小便 310 mL / 8 hr。",
            "Drain emptied, 280 mL, cloudier, yellow-green. Urine 310 mL / 8 hr.",
          ),
        ],
        ["701-1 05:10", tr("自解小便順暢，傷口乾。", "Voiding well, wound dry.")],
        ["702-1 05:30", tr("自行下床於走廊活動，無不適。", "Up and walking in the hallway on his own, no complaints.")],
      ];
    }),
  },
  pathway: {
    title: tr("臨床路徑表", "Clinical pathway"),
    sub: tr("胃切除術後（貼在護理站牆上）", "Post-gastrectomy (posted on the nurses’ station wall)"),
    ch: "pathway",
    doc: doc(() => {
      S.seen.add("pathway");
      S.seen.add("pathway_fine");
      return [
        ["POD1", tr("下床活動、拔尿管、肺部復健", "Out of bed, Foley out, pulmonary rehab")],
        ["POD2–3", tr("拔 NG tube（依醫囑）、排氣後評估", "NG tube out (per order), reassess after flatus")],
        ["POD4", tr("開始喝水 30 mL/hr", "Start water 30 mL/hr")],
        ["POD5", tr("流質飲食", "Liquid diet")],
        ["POD6", tr("半流質、拔引流管（引流量 < 50 mL 且清澈）", "Soft diet; drain out (output < 50 mL and clear)")],
        ["POD7", tr("出院衛教、出院", "Discharge teaching, discharge")],
        [
          tr("暫停條件（小字）", "Hold criteria (fine print)"),
          tr(
            "出現發燒 ≥ 38°C、心跳 > 100、引流液量或性狀改變、腹痛加劇時，暫停路徑並通知醫師。",
            "Fever ≥ 38°C, HR > 100, change in drain output or character, or worsening abdominal pain: hold the pathway and notify the physician.",
          ),
        ],
      ];
    }),
  },
  census: {
    title: tr("病房白板", "Ward whiteboard"),
    sub: tr("今天", "Today"),
    ch: "census",
    doc: doc(() => {
      S.seen.add("census");
      return [
        [
          tr("701-1 林○○", "701-1 Lin"),
          tr("R't TEP POD1。今天出院（clerk 衛教）", "R’t TEP POD1. Discharge today (clerk does teaching)"),
        ],
        [
          tr("701-2 陳○○", "701-2 Chen"),
          tr("STG + Roux-en-Y POD4。開始喝水 30 mL/hr（路徑）", "STG + Roux-en-Y POD4. Start water 30 mL/hr (pathway)"),
        ],
        [tr("702-1 蔡○○", "702-1 Tsai"), tr("Hepatectomy POD2。拔 Foley、下床", "Hepatectomy POD2. Foley out, OOB")],
        [tr("晨會", "Conference"), tr("07:30 會議室", "07:30 conference room")],
        [tr("今日刀", "Today’s OR"), tr("江主任 OR 3　08:30 Whipple", "Dr. Chiang, OR 3 · 08:30 Whipple")],
      ];
    }),
  },
  cart: {
    title: tr("換藥車", "Dressing cart"),
    sub: tr("護理站旁", "By the nurses’ station"),
    ch: "cart",
    choices() {
      return [
        C(
          tr("拿一包換藥包、手套、紗布", "Take a dressing kit, gloves, gauze"),
          () => {
            S.kit = true;
            me("cart", tr("（拿換藥包）", "(Takes a dressing kit)"));
            note(
              tr(
                "你拿了換藥包、無菌手套、紗布和透氣膠帶。",
                "You take a dressing kit, sterile gloves, gauze and paper tape.",
              ),
            );
          },
          S.kit,
        ),
      ];
    },
  },
  phone: {
    title: tr("護理站電話", "Station phone"),
    sub: tr("要打給誰？", "Who do you call?"),
    ch: "phone",
    choices() {
      const rNear = !S.vsGone && !!findEnt("gs_r") && !findEnt("gs_r")?.hidden;
      return [
        C(
          tr("打給阿凱學長", "Call Kai"),
          () => {
            me("phone", tr("（撥阿凱的公務機）", "(Dials Kai’s work phone)"));
            openDialog("r");
          },
          S.accepted || (rNear && S.conf !== "done"),
        ),
        C(
          tr("打給江主任", "Call Dr. Chiang"),
          () => {
            me("phone", tr("（撥江主任的手機）", "(Dials Dr. Chiang’s cell)"));
            openDialog("vs");
          },
          S.accepted || S.conf === "open",
        ),
      ];
    },
  },
};

/* ---------- level ---------- */

export const GS1: Level = {
  id: "gs-ward",
  no: tr("一", "1"),
  short: tr("第一關　病房", "Level 1 · Ward"),
  title: tr("病房：照路徑走", "Ward: Follow the Pathway"),
  place: tr("外科病房 7 樓", "Surgical ward, 7F"),
  blurb: tr(
    "早上七點巡房，七點半晨會。胃切除第四天，路徑說今天開始喝水。教學關。",
    "Pre-rounds at 7:00, morning conference at 7:30. Day four after a gastrectomy, and the pathway says he starts water today. Tutorial level.",
  ),
  cast: ["chen", "chen_dau", "zhiwei", "tsai", "gs_nurse", "np", "gs_r", "gs_vs"],
  intro: [
    tr(
      "週一早上 07:00，一般外科第一天。你接了三床：701-1 疝氣術後要出院的志偉、701-2 胃癌術後第四天的陳伯伯、702-1 肝切除術後第二天的蔡先生。",
      "Monday, 07:00, your first day on General Surgery. You have three beds: Chih-Wei in 701-1, going home after hernia surgery; Mr. Chen in 701-2, day four after surgery for stomach cancer; and Mr. Tsai in 702-1, day two after a liver resection.",
    ),
    tr(
      "七點半在會議室晨會，江主任會聽你報告。在那之前先 pre-round：生命徵象、I/O、引流、傷口、問病人。大夜班的欣怡在護理站等著交班。",
      "At 7:30 there’s morning conference in the conference room, where Dr. Chiang will hear you present. Before that, pre-round: vitals, I/O, drains, wounds, and talk to the patients. Hsin-Yi, the night-shift nurse, is waiting at the nurses’ station to hand off.",
    ),
  ],
  start: 7 * 60,
  maxT: 60,
  night: false,
  idleSec: 13,
  map: [
    "####################",
    "#aaaaaaaa#ccccccccc#",
    "#aaaaaaaa#ccccccccc#",
    "#aaaaaaaa#ccccccccc#",
    "#aaaaaaaa#ccccccccc#",
    "####a#######c#######",
    "#hhhhhhhhhhhhhhhhhh#",
    "#hhhhhhhhhhhhhhhhhh#",
    "#nnnnnnnnn####d#####",
    "#nnnnnnnnn#dddddddd#",
    "#nnnnnnnnn#dddddddd#",
    "####################",
  ],
  zones: {
    a: ["#e6ece8", "#d2dcd6", "#dde5e0"],
    c: ["#e4e0ec", "#d0cadc", "#dad5e5"],
    h: ["#ece6d6", "#d9d0bb", "#e2dac7"],
    n: ["#e3eaf6", "#ccd6ea", "#d6dff0"],
    d: ["#e8ece2", "#d4dacb", "#dee3d6"],
  },
  windows: [
    [2, 0],
    [6, 0],
    [14, 0],
    [16, 11],
  ],
  exits: [DOOR],
  furniture: WARD_FURNITURE,
  chName: {
    chen: tr("陳伯伯", "Mr. Chen"),
    zhiwei: tr("志偉", "Chih-Wei"),
    tsai: tr("蔡先生", "Mr. Tsai"),
    nurse: tr("欣怡", "Hsin-Yi"),
    np: tr("佩珊學姊", "Pei-Shan"),
    r: tr("阿凱", "Kai"),
    vs: tr("江主任", "Dr. Chiang"),
    emr: tr("電腦", "Computer"),
    labs: tr("檢驗", "Labs"),
    meds: tr("用藥", "Meds"),
    opnote: tr("手術紀錄", "Op notes"),
    nursenote: tr("護理紀錄", "Nursing notes"),
    pathway: tr("臨床路徑", "Pathway"),
    census: tr("白板", "Whiteboard"),
    cart: tr("換藥車", "Dressing cart"),
    phone: tr("電話", "Phone"),
    sys: tr("病房", "Ward"),
  },
  objects: [
    {
      id: "bedZ",
      type: "wbed",
      x: ZHIWEI_BED[0],
      y: ZHIWEI_BED[1],
      h: 2,
      label: tr("701-1 志偉", "701-1 Chih-Wei"),
      act: "zhiwei",
    },
    { id: "cabZ", type: "cabinet", x: 3, y: 1 },
    { id: "curtain", type: "curtain", x: 4, y: 1, h: 2 },
    { id: "pole", type: "ivpole", x: 5, y: 1 },
    {
      id: "bedC",
      type: "wbed",
      x: CHEN_BED[0],
      y: CHEN_BED[1],
      h: 2,
      label: tr("701-2 陳伯伯", "701-2 Mr. Chen"),
      act: "chen",
    },
    { id: "cabC", type: "cabinet", x: 7, y: 1 },
    { id: "san1", type: "sanitizer", x: 8, y: 4, label: tr("乾洗手", "Sanitizer"), act: "wash" },
    { id: "screen", type: "screen", x: 12, y: 1, w: 4 },
    {
      id: "table",
      type: "conftable",
      x: 12,
      y: 2,
      w: 4,
      label: tr("晨會（江主任）", "Conference (Dr. Chiang)"),
      act: "vs",
    },
    { id: "chairs", type: "chairrow", x: 12, y: 3, w: 4, walk: true },
    { id: "wb", type: "whiteboard", x: 18, y: 1 },
    { id: "census", type: "census", x: 15, y: 5, w: 2, label: tr("病房白板", "Ward whiteboard") },
    { id: "san2", type: "sanitizer", x: 9, y: 6, label: tr("乾洗手", "Sanitizer"), act: "wash" },
    { id: "lift", type: "elevator", x: 18, y: 5 },
    { id: "plant", type: "plant", x: 18, y: 7 },
    { id: "pathway", type: "pathway", x: 1, y: 9, label: tr("臨床路徑表", "Clinical pathway") },
    { id: "counter", type: "counter", x: 2, y: 8, w: 6 },
    { id: "phone", type: "phone", x: 2, y: 8, label: tr("電話", "Phone") },
    { id: "emr", type: "computer", x: 6, y: 8, label: tr("護理站電腦", "Station computer") },
    {
      id: "nurseDesk",
      type: "counter",
      x: 4,
      y: 8,
      label: tr("欣怡（交班）", "Hsin-Yi (handoff)"),
      act: "nurse",
      walk: false,
    },
    { id: "medcart", type: "medcart", x: 6, y: 10 },
    { id: "cart", type: "dresscart", x: 8, y: 10, label: tr("換藥車", "Dressing cart") },
    {
      id: "bedT",
      type: "wbed",
      x: TSAI_BED[0],
      y: TSAI_BED[1],
      h: 2,
      label: tr("702-1 蔡先生", "702-1 Mr. Tsai"),
      act: "tsai",
    },
    { id: "poleT", type: "ivpole", x: 12, y: 9 },
    { id: "cabT", type: "cabinet", x: 15, y: 9 },
    { id: "san3", type: "sanitizer", x: 17, y: 9, label: tr("乾洗手", "Sanitizer"), act: "wash" },
  ],
  labels: {
    chen: tr("701-2 陳伯伯、雅婷", "701-2 Mr. Chen, Ya-Ting"),
    zhiwei: tr("701-1 志偉", "701-1 Chih-Wei"),
    tsai: tr("702-1 蔡先生", "702-1 Mr. Tsai"),
    nurse: tr("欣怡（大夜班）", "Hsin-Yi (night shift)"),
    np: tr("佩珊學姊", "Pei-Shan"),
    r: tr("阿凱", "Kai"),
    vs: tr("江主任", "Dr. Chiang"),
    wash: tr("乾洗手", "Sanitizer"),
  },
  help: {
    chen: tr(
      "胃癌術後第四天。問診、生命徵象、肚子、傷口、引流",
      "POD4 after stomach cancer surgery. History, vitals, abdomen, wound, drain",
    ),
    zhiwei: tr(
      "疝氣術後第一天，今天出院，要做衛教",
      "POD1 after hernia repair, going home today; needs discharge teaching",
    ),
    tsai: tr(
      "肝切除術後第二天，對照組：正常的術後長什麼樣子",
      "POD2 after liver resection. Your control: what a normal post-op course looks like",
    ),
    nurse: tr("交班、量生命徵象、抽血、送檢、禁食", "Handoff, vitals, labs, samples, NPO"),
    np: tr("專師：管路徑、出院、醫囑", "NP: runs the pathway, discharges, orders"),
    r: tr("住院醫師：用藥、輸液、CT 要他同意", "Resident: meds, IV fluids and CT need his OK"),
    vs: tr("07:30 晨會在會議室，也可以打電話", "07:30 conference in the conference room; you can also call him"),
    emr: tr("TPR 單、檢驗、用藥、手術紀錄、護理紀錄", "TPR sheets, labs, meds, op notes, nursing notes"),
    pathway: tr("胃切除臨床路徑（記得看小字）", "Gastrectomy pathway (read the fine print)"),
    census: tr("今天的病房計畫", "Today’s plan for the ward"),
    cart: tr("換藥包（看傷口前先拿）", "Dressing kits (grab one before checking a wound)"),
    phone: tr("打給阿凱或江主任", "Call Kai or Dr. Chiang"),
    wash: tr("摸病人前、換下一床前", "Before touching a patient, and before the next bed"),
  },
  helpRules: [
    tr(
      "這是外科的教學關：七點半前把三床看完（生命徵象的趨勢、I/O、引流、傷口、問病人），七點半到會議室晨會。",
      "This is the surgery tutorial: see all three beds before 7:30 (vital-sign trends, I/O, drains, wounds, talking to patients), then go to the conference room for morning conference at 7:30.",
    ),
    tr(
      "晨會報告第一句講最要緊的事。江主任沒時間聽流水帳。",
      "At conference, lead with what matters most. Dr. Chiang has no time for a rambling story.",
    ),
    tr("臨床路徑是給沒事的病人走的。", "Clinical pathways are for patients who are doing fine."),
    tr(
      "摸病人前先乾洗手；看傷口前先去換藥車拿換藥包。",
      "Sanitize your hands before touching a patient; grab a dressing kit from the dressing cart before checking a wound.",
    ),
    tr(
      "禁食、抽血、送檢可以請護理師或專師做；用藥、輸液、CT 要阿凱學長或主任同意。",
      "Nurses and the NP can do NPO, blood draws and sending samples; meds, IV fluids and CT need Kai or Dr. Chiang to sign off.",
    ),
  ],
  player: [9, 7],
  ents: () => [
    mkEnt({ k: "gs_nurse", id: "gs_nurse", x: 4, y: 9, act: "nurse", face: "up" }),
    mkEnt({ k: "chen_dau", id: "chen_dau", x: 7, y: 2, act: "chen", face: "left" }),
    mkEnt({ k: "tsai", id: "tsai", x: 12, y: 7, act: "tsai", dir: "right" }),
  ],
  init: (): Vars => ({
    revealed: new Set(),
    taught: new Set(),
    seen: new Set(),
    handover: false,
    orders: {},
    npoAt: null,
    drankAt: null,
    held: false,
    okedWater: false,
    fluidAt: null,
    abxAt: null,
    abxDrug: null,
    cultureBeforeAbx: null,
    rApproved: false,
    rApprovedAt: null,
    kit: false,
    hands: null,
    handSlips: 0,
    vitals: [],
    rigorAt: null,
    rigorSeen: false,
    vomitAt: null,
    shockAt: null,
    zhiweiOut: false,
    dischargeBy: null,
    etvFixed: false,
    tsaiInBed: false,
    conf: "wait",
    confOpenAt: null,
    headline: null,
    confTalks: 0,
    accepted: false,
    acceptedAt: null,
    acceptedVia: null,
    rPresented: false,
    vsHere: false,
    vsGone: false,
    ctAt: null,
    endAt: null,
  }),
  reset() {
    tprWho = "chen";
    milkUntil = 0;
  },
  dialogs,
  zooms: {
    chen: () => bedZoom("chen"),
    zhiwei: () => (S.zhiweiOut ? null : bedZoom("zhiwei")),
    tsai: () => (S.tsaiInBed ? bedZoom("tsai") : null),
  },
  quick: {
    wash,
  },

  onStart: () =>
    eventBanner("07:00", tr("外科病房 7 樓・晨會前的 pre-round", "Surgical ward 7F · pre-rounds before conference")),
  beforeInteract(id) {
    if (id === "vs" && !S.vsHere) {
      note(
        tr(
          "會議室還沒有人。晨會 07:30，主任還在路上（要找他可以打電話）。",
          "The conference room is empty. Conference is at 07:30 and Dr. Chiang is still on his way (call him if you need him).",
        ),
      );
      return false;
    }
    if (id === "vs" && S.vsGone) {
      note(
        tr(
          "會議室空了。主任去刀房了，要找他得打電話。",
          "The conference room has emptied out. Dr. Chiang has gone to the OR; you’ll have to call him.",
        ),
      );
      return false;
    }
    if (id === "tsai" && !S.tsaiInBed && zone(P.x, P.y) === "d") {
      note(
        tr(
          "蔡先生不在床上，推著點滴架在走廊散步。",
          "Mr. Tsai isn’t in bed; he’s out walking the hall with his IV pole.",
        ),
      );
      return false;
    }
    if (id === "zhiwei" && S.zhiweiOut) {
      note(tr("志偉出院了，床單已經換好。", "Chih-Wei has gone home; the sheets are already changed."));
      return false;
    }
    return undefined;
  },
  onStep(x, y) {
    if (zone(x, y) === "c" && S.vsHere && !S.vsGone && S.conf === "ready" && S.t >= VS_ARRIVE_T + 2) openConf();
    if (zone(x, y) === "a" && S.rigorAt !== null && S.t < S.rigorAt + 4 && !S.rigorSeen) {
      S.rigorSeen = true;
      finding(
        tr("寒顫", "Rigors"),
        tr(`${clock()} 陳伯伯全身發抖、牙齒打顫`, `${clock()} Mr. Chen shaking all over, teeth chattering`),
      );
    }
  },
  tick(t) {
    if (t === 1)
      ask(
        "nurse",
        "gs_nurse",
        tr(
          "學弟早！你三床的狀況我先跟你交班，我八點下班喔。",
          "Morning! Let me hand off your three beds. I’m off at eight.",
        ),
        {
          id: "handover",
          text: tr("欣怡在等你交班", "Hsin-Yi is waiting to hand off"),
          dlg: "nurse",
          clearOn: "manual",
          icon: tr("！", "!"),
        },
      );
    if (t === 3) {
      spawnAt("gs_r", "r", DOOR, [11, 7]);
      post(
        "r",
        "npc",
        "gs_r",
        tr(
          "早，學弟。你今天 primary 三床。七點半晨會主任會問，vital、I/O、drain、傷口都要看。志偉今天出院，衛教你來。我去趕 note。",
          "Morning. You’re primary on three beds today. Dr. Chiang will grill you at 7:30 conference, so check vitals, I/O, drains, wounds, all of it. Chih-Wei goes home today; you do his teaching. I’ve got notes to catch up on.",
        ),
      );
      addWait({
        id: "conf",
        text: tr("07:30 會議室晨會", "07:30 morning conference"),
        untilT: CONF_T,
        from: t,
        clearOn: "manual",
        icon: "⏰",
      });
    }
    if (t === 5 && !S.zhiweiOut)
      ask(
        "zhiwei",
        "zhiwei",
        tr(
          "醫生！我今天可以回家了對吧？要注意什麼？",
          "Doc! I get to go home today, right? Anything I should watch out for?",
        ),
        {
          id: "zhiwei",
          text: tr("志偉在等出院衛教", "Chih-Wei is waiting for discharge teaching"),
          dlg: "zhiwei",
          clearOn: "manual",
        },
      );
    if (t === 8) {
      spawnAt("np", "np", DOOR, [7, 9]);
      post(
        "np",
        "npc",
        "np",
        tr(
          "早～學弟，今天志偉出院、陳伯伯 POD4 開始喝水、蔡先生拔尿管。我先去印單子。",
          "Morning! Today: Chih-Wei goes home, Mr. Chen starts water on POD4, Mr. Tsai’s Foley comes out. I’m off to print forms.",
        ),
      );
    }
    if (t === 12 && S.npoAt === null && !S.held && S.drankAt === null)
      ask(
        "np",
        "np",
        tr(
          "學弟，陳伯伯路徑今天開始喝水，30 cc 一小時，你去跟他講一下？",
          "Hey, Mr. Chen’s pathway says he starts water today, 30 cc an hour. Can you go tell him?",
        ),
        {
          id: "water",
          text: tr("佩珊：叫陳伯伯開始喝水", "Pei-Shan: start Mr. Chen on water"),
          dlg: "np",
          untilT: WATER_T,
          clearOn: "manual",
        },
      );
    if (t === 14 && !S.tsaiInBed) {
      const e = ent("tsai");
      walkEnt(e, [TSAI_BED[0], TSAI_BED[1] + 2 > 10 ? 10 : TSAI_BED[1] + 2]);
      e.onArrive = () => {
        e.hidden = true;
        S.tsaiInBed = true;
        post(
          "tsai",
          "narr",
          null,
          tr("蔡先生走回 702，自己爬上床。", "Mr. Tsai walks back to 702 and climbs into bed on his own."),
        );
      };
    }
    if (t === RIGOR_T) {
      S.rigorAt = t;
      eventBanner(tr("寒顫", "Rigors"), tr("701-2 陳伯伯在發抖", "701-2 Mr. Chen is shaking"), true);
      post(
        "chen",
        "alert",
        "chen_dau",
        tr(
          "醫生！我爸又在發抖了，跟半夜一樣，牙齒一直打顫！",
          "Doctor! My dad’s shaking again, just like last night. His teeth won’t stop chattering!",
        ),
      );
      S.revealed.add("rigor");
      if (zone(P.x, P.y) === "a") {
        S.rigorSeen = true;
        finding(
          tr("寒顫", "Rigors"),
          tr(`${clock()} 陳伯伯全身發抖、牙齒打顫`, `${clock()} Mr. Chen shaking all over, teeth chattering`),
        );
      }
    }
    if (t === 20 && !S.handover)
      post(
        "nurse",
        "npc",
        "gs_nurse",
        tr(
          "學弟～交班要不要聽一下？陳伯伯昨晚的狀況我想跟你講。",
          "Hey, want to hear handoff? There’s something about Mr. Chen’s night I want to tell you.",
        ),
      );
    if (t === 21 && !S.zhiweiOut && S.taught.size < 3)
      post(
        "np",
        "npc",
        "np",
        tr(
          "學弟，志偉的衛教好了沒？他家人九點來接。",
          "Is Chih-Wei’s teaching done yet? His family’s picking him up at nine.",
        ),
      );
    if (t === WATER_T && S.npoAt === null && !S.held && S.drankAt === null) {
      post(
        "np",
        "npc",
        "np",
        tr(
          "陳伯伯的水我照路徑先讓他開始喝了喔，你忙你的。",
          "I went ahead and started Mr. Chen on water per the pathway. You carry on.",
        ),
      );
      drink(tr("佩珊學姊叫雅婷", "At Pei-Shan’s word, Ya-Ting"));
    }
    if (S.drankAt !== null && t === S.drankAt + 6 && S.vomitAt === null && S.npoAt === null) {
      S.vomitAt = t;
      eventBanner(tr("嘔吐", "Vomiting"), tr("701-2 陳伯伯", "701-2 Mr. Chen"), true);
      post(
        "chen",
        "alert",
        "chen_dau",
        tr(
          "醫生！他喝完水說肚子更痛，剛剛吐了，吐出來黃綠色的！",
          "Doctor! After the water he said his stomach hurt more, and he just threw up. It was yellow-green!",
        ),
      );
      finding(
        tr("嘔吐", "Vomiting"),
        tr(
          `${clock()} 喝水後嘔吐，黃綠色，腹痛加劇`,
          `${clock()} vomited after drinking water, yellow-green; abdominal pain worse`,
        ),
      );
    }
    if (t === VS_ARRIVE_T) vsArrive();
    if (t === 27 && S.conf === "ready") {
      walkEnt(ent("gs_r"), [11, 2]);
      post("r", "npc", "gs_r", tr("學弟，晨會了，走！", "Conference time. Let’s go!"));
    }
    if (t === CONF_T && S.conf === "ready") {
      if (zone(P.x, P.y) === "c") openConf();
      else
        ask(
          "vs",
          "gs_r",
          tr(
            "（從會議室探頭）學弟！主任在等了！",
            "(Pokes his head out of the conference room) Hey! Dr. Chiang’s waiting!",
          ),
          {
            id: "conf",
            text: tr("晨會開始了！到會議室", "Conference has started! Go to the conference room"),
            untilT: R_PRESENT_T,
            clearOn: "manual",
            urgent: true,
            icon: "⏰",
          },
        );
    }
    if (t === CONF_T + 4 && S.conf === "open" && S.confTalks === 0) {
      post(
        "vs",
        "npc",
        "gs_vs",
        tr("……講啊。三床，從最要緊的開始。", "…Well? Three beds. Start with the most urgent."),
      );
      S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
    }
    if (t === R_PRESENT_T) rPresent();
    if (t === CONF_END_T) vsLeave();
    if (t === 28 && !S.zhiweiOut) discharge("np");

    // orders
    for (const [k, o] of Object.entries(S.orders) as [OrderId, Order][]) {
      if (o.done || t < o.ready) continue;
      o.done = true;
      if (k === "labs") {
        const txt = tr(
          `lactate ${t >= 45 ? "4.2" : "3.6"} mmol/L、CRP 21.6 mg/dL、Cr 1.5、T-bil 1.6`,
          `lactate ${t >= 45 ? "4.2" : "3.6"} mmol/L, CRP 21.6 mg/dL, Cr 1.5, T-bil 1.6`,
        );
        finding(tr("加驗", "Add-on labs"), txt);
        post("nurse", "result", "gs_nurse", tr(`陳伯伯急件：${txt}。`, `Mr. Chen’s stat labs: ${txt}.`));
      } else if (k === "drainlab") {
        const txt = tr(
          "Bilirubin 9.8 mg/dL（血清 1.6）、Amylase 2,150 U/L",
          "Bilirubin 9.8 mg/dL (serum 1.6), Amylase 2,150 U/L",
        );
        finding(tr("引流液", "Drain fluid"), txt);
        post("nurse", "result", "gs_nurse", tr(`引流液檢驗出來了：${txt}。`, `Drain fluid results are back: ${txt}.`));
      } else if (k === "ct") {
        S.ctAt = t;
        const txt = tr(
          "十二指腸殘端旁 6 cm 積液含氣泡、周圍脂肪浸潤；右側少量肋膜積液",
          "6 cm collection with gas bubbles beside the duodenal stump, surrounding fat stranding; small right pleural effusion",
        );
        finding("CT", txt);
        post(
          "sys",
          "result",
          null,
          tr(`放射科電話：陳伯伯 CT ${txt}。`, `Radiology calls: Mr. Chen’s CT shows ${txt}.`),
        );
      } else if (k === "vitals") {
        const v = chenV();
        S.vitals.push({ who: "chen", t, v });
        post("nurse", "result", "gs_nurse", tr(`陳伯伯：${vtxt(v)}。`, `Mr. Chen: ${vtxt(v)}.`));
      } else if (k === "bcx")
        post("nurse", "npc", "gs_nurse", tr("Culture 兩套抽好了。", "Two sets of cultures drawn."));
    }

    // shock
    if (S.shockAt === null && !S.accepted && chenV(t).sbp < 90) {
      S.shockAt = t;
      eventBanner(tr("敗血性休克", "Septic shock"), tr("701-2 陳伯伯", "701-2 Mr. Chen"), true);
      post(
        "chen",
        "alert",
        "chen_dau",
        tr("醫生！我爸叫不太醒，嘴唇好白！", "Doctor! I can barely wake my dad, and his lips are so pale!"),
      );
      post(
        "nurse",
        "alert",
        "gs_nurse",
        tr(
          `陳伯伯 BP ${chenV(t).sbp}/${chenV(t).dbp}、HR ${chenV(t).hr}，我已經 call 學長了！`,
          `Mr. Chen’s BP ${chenV(t).sbp}/${chenV(t).dbp}, HR ${chenV(t).hr}. I’ve already called Kai!`,
        ),
      );
      if (S.vsHere && !S.vsGone) walkEnt(ent("gs_vs"), [6, 3]);
      const r = findEnt("gs_r");
      if (r && !r.hidden) walkEnt(r, [5, 3]);
      else spawnAt("gs_r", "r", DOOR, [5, 3]);
      S.endAt = t + 4;
    }

    if (S.endAt === t) {
      if (S.shockAt !== null) endC();
      else if (S.accepted) {
        if (S.acceptedVia === "conf" && S.headline === "lead") endA();
        else endB();
      }
    }
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("八點查房", "08:00 Rounds"),
      [
        {
          who: "np",
          text: tr(
            "主任下刀前交代：陳伯伯先 NPO、抽 culture、排 CT。",
            "Dr. Chiang’s orders before heading into the OR: Mr. Chen NPO, draw cultures, book a CT.",
          ),
        },
        {
          who: "np",
          text: tr(
            "學弟，路徑是給沒事的人走的。下次心跳 116 要講。",
            "Pathways are for patients who are fine. Next time a heart rate hits 116, say something.",
          ),
        },
      ],
      tr(
        "時間到。陳伯伯的處理被延到主任查房之後。",
        "Time’s up. Mr. Chen’s treatment was pushed back until after the attending’s rounds.",
      ),
    ),

  hud() {
    const last = measured("chen").at(-1);
    const conf =
      S.conf === "open"
        ? tr("進行中", "In progress")
        : S.conf === "done"
          ? S.accepted
            ? tr("主任已決定", "Decided")
            : tr("結束", "Over")
          : tr("07:30 會議室", "07:30 conf. room");
    return [
      [
        tr("陳伯伯", "Mr. Chen"),
        last
          ? tr(
              `HR ${last.v.hr}・${last.v.temp}°C（${clock(last.t)}）`,
              `HR ${last.v.hr} · ${last.v.temp}°C (${clock(last.t)})`,
            )
          : tr("還沒量", "Not taken"),
      ],
      [
        tr("飲食", "Diet"),
        S.npoAt !== null
          ? tr("禁食", "NPO")
          : S.drankAt !== null
            ? tr("已喝水", "Drank water")
            : tr("路徑：今天喝水", "Pathway: water today"),
      ],
      [tr("晨會", "Conference"), conf],
    ];
  },
  urgent: () =>
    S.shockAt !== null || (S.rigorAt !== null && S.t < S.rigorAt + 4) || (S.conf === "ready" && S.t >= CONF_T),
  outcomeText: () => (S.accepted ? tr("主任決定處置", "Attending made the call") : tr("巡房結束", "Rounds over")),

  drawBelow(c, now) {
    if (!S.zhiweiOut)
      drawMapPatient(c, ZHIWEI_BED[0] * T, ZHIWEI_BED[1] * T, { ...PT_LOOK.zhiwei, sitting: true }, now);
    const shaking = (S.rigorAt !== null && S.t < S.rigorAt + 4) || S.shockAt !== null;
    drawMapPatient(c, CHEN_BED[0] * T, CHEN_BED[1] * T, { ...PT_LOOK.chen, shake: shaking }, now);
    if (S.npoAt !== null) {
      R(c, "#ffffff", CHEN_BED[0] * T + 2, CHEN_BED[1] * T - 7, 13, 7);
      ptext(c, "NPO", CHEN_BED[0] * T + 3, CHEN_BED[1] * T - 6, "#c0394a");
    }
    // drain bag hanging off 陳伯伯's bed
    R(c, "#eef3f6", CHEN_BED[0] * T + 13, CHEN_BED[1] * T + 20, 4, 6);
    R(c, "#93a03a", CHEN_BED[0] * T + 13, CHEN_BED[1] * T + 23, 4, 3);
    if (S.tsaiInBed) drawMapPatient(c, TSAI_BED[0] * T, TSAI_BED[1] * T, { ...PT_LOOK.tsai, sitting: true }, now);
    // 蔡先生 pushes an IV pole while walking
    const tsai = findEnt("tsai");
    if (tsai && !tsai.hidden) {
      const px = Math.round(tsai.fx * T) + 13;
      const py = Math.round(tsai.fy * T) - 6;
      R(c, "#9aa0b2", px, py, 1, 18);
      R(c, "#e6f2f8", px - 2, py, 4, 5);
    }
  },

  graderCase: tr(
    `教學關（一般外科病房）。三床：志偉 34 歲 TEP 疝氣修補 POD1 今天出院（clerk 負責出院衛教）；陳伯伯 66 歲胃癌 subtotal gastrectomy＋Roux-en-Y POD4；蔡先生 58 歲 HBV-HCC 右肝後段切除 POD2，恢復正常（對照組；住院後 Entecavir 沒有續開是加分的用藥整合點）。真相：陳伯伯十二指腸殘端滲漏、腹內感染、早期敗血症——TPR 單上半夜起心跳 84→96→108→116、體溫爬到 37.9、呼吸 24、大夜尿量只有 310 mL、引流從每班 25–35 mL 清澈變成 280 mL 混濁黃綠（靠近十二指腸殘端的 drain），右上腹壓痛合併 guarding，傷口本身乾淨；夜班把它當成傷口痛；他半夜寒顫但叫女兒不要講（想趕快出院參加孫子婚禮）；07:17 會再寒顫一次。今早例行 CBC WBC 17,900、band 7%。陷阱：胃切除路徑 POD4 開始喝水，專師與 R 都照路徑走；路徑表小字寫著發燒、心跳 >100、引流改變時要暫停。若沒人阻止，07:25 專師會讓他開始喝水，喝水後腹痛加劇、嘔吐。正確處置：禁食、抽血（CBC、CRP、肝腎、lactate）、血液培養（在抗生素前）、引流液送 bilirubin／amylase、輸液、廣效抗生素（如 piperacillin/tazobactam；只用 cefazolin 不足）、安排含顯影劑 CT，並在 07:30 晨會第一句就向主治報告懷疑滲漏。未處理時約 07:45 進展成敗血性休克。其他：摸病人前乾洗手、看傷口前拿換藥包。`,
    `Tutorial level (general surgery ward). Three beds: Chih-Wei, 34, POD1 after TEP hernia repair, going home today (the clerk is responsible for discharge teaching); Mr. Chen, 66, gastric cancer, POD4 after subtotal gastrectomy + Roux-en-Y; Mr. Tsai, 58, HBV-related HCC, POD2 after right posterior sectionectomy, recovering normally (the control case; catching that his entecavir was not continued on admission is a bonus medication-reconciliation point). Truth: Mr. Chen has a duodenal stump leak, intra-abdominal infection and early sepsis. On the TPR sheet, from the middle of the night his heart rate went 84→96→108→116, temperature crept up to 37.9, RR 24, night-shift urine output only 310 mL, and the drain (the one near the duodenal stump) went from 25-35 mL per shift, clear, to 280 mL, cloudy yellow-green; RUQ tenderness with guarding, while the wound itself is clean. The night shift took it for wound pain. He had rigors in the night but told his daughter not to say anything (he wants to get out in time for his grandson's wedding); he will have another rigor at 07:17. This morning's routine CBC: WBC 17,900, bands 7%. The trap: the gastrectomy pathway says start water on POD4, and both the NP and the resident follow the pathway; the fine print on the pathway says to hold it for fever, HR >100, or a change in the drain. If nobody stops it, the NP starts him on water at 07:25, after which his abdominal pain worsens and he vomits. Correct management: NPO, labs (CBC, CRP, liver and renal function, lactate), blood cultures (before antibiotics), drain fluid for bilirubin/amylase, IV fluids, broad-spectrum antibiotics (e.g. piperacillin/tazobactam; cefazolin alone is inadequate), a contrast-enhanced CT, and leading with the suspected leak in the very first sentence when presenting to the attending at the 07:30 morning conference. Untreated, he progresses to septic shock around 07:45. Other: hand sanitizing before touching patients, getting a dressing kit before looking at a wound.`,
  ),
  rubric: [
    tr(
      "Pre-round 資料收集：交班、TPR 趨勢（心跳、體溫、呼吸）、I/O 與尿量、引流量與性狀",
      "Pre-rounds data gathering: handoff, TPR trends (HR, temp, RR), I/O and urine output, drain output and character",
    ),
    tr(
      "陳伯伯的病史與身體檢查：問出寒顫（含從女兒）、疼痛位置不是傷口、右上腹 guarding、傷口本身乾淨",
      "Mr. Chen’s history and exam: elicits the rigors (including from his daughter), pain that isn’t from the wound, RUQ guarding, wound itself clean",
    ),
    tr(
      "識破路徑陷阱：在懷疑滲漏時禁食，沒有讓他喝水",
      "Sees through the pathway trap: makes him NPO on suspicion of a leak instead of letting him drink",
    ),
    tr(
      "Workup 與初步處置：抽血含 lactate、血液培養先於抗生素、引流液 bilirubin／amylase、輸液、適當的廣效抗生素、CT",
      "Workup and initial management: labs including lactate, blood cultures before antibiotics, drain fluid bilirubin/amylase, IV fluids, appropriate broad-spectrum antibiotics, CT",
    ),
    tr(
      "晨會報告：第一句講最要緊的事，有數據支持、有計畫",
      "Morning conference presentation: leads with what matters most, backed by data, with a plan",
    ),
    tr(
      "志偉的出院衛教（傷口、活動與提重物、止痛、警訊、回診）",
      "Chih-Wei’s discharge teaching (wound, activity and lifting, pain control, warning signs, follow-up)",
    ),
    tr(
      "其他：蔡先生的評估與用藥整合、手部衛生、與病人和家屬溝通",
      "Other: Mr. Tsai’s assessment and med reconciliation, hand hygiene, communication with patients and family",
    ),
  ],
  facts: () => [
    tr(`交班：${S.handover ? "有聽" : "沒聽"}`, `Handoff: ${S.handover ? "heard it" : "skipped it"}`),
    tr(
      `陳伯伯 TPR 心跳趨勢：${S.seen.has("pulse_chen") ? "有注意到" : "沒注意到"}`,
      `Mr. Chen's TPR heart-rate trend: ${S.seen.has("pulse_chen") ? "noticed" : "not noticed"}`,
    ),
    tr(
      `陳伯伯引流：${S.seen.has("drain_chen") ? "看過引流袋" : S.seen.has("drainrow_chen") ? "只看過 I/O 紀錄" : "沒看"}`,
      `Mr. Chen's drain: ${S.seen.has("drain_chen") ? "looked at the drain bag" : S.seen.has("drainrow_chen") ? "only saw the I/O record" : "not checked"}`,
    ),
    tr(
      `寒顫：${S.revealed.has("rigor") || S.rigorSeen ? "知道" : "不知道"}`,
      `Rigors: ${S.revealed.has("rigor") || S.rigorSeen ? "known" : "not known"}`,
    ),
    tr(
      `喝水：${S.drankAt !== null ? `${clock(S.drankAt)} 喝了（${S.okedWater ? "clerk 同意" : "專師照路徑"}）` : "沒有"}`,
      `Water: ${S.drankAt !== null ? `drank at ${clock(S.drankAt)} (${S.okedWater ? "clerk agreed" : "NP, per pathway"})` : "no"}`,
    ),
    tr(`禁食：${S.npoAt !== null ? clock(S.npoAt) : "沒有"}`, `NPO: ${S.npoAt !== null ? clock(S.npoAt) : "no"}`),
    tr(
      `抗生素：${S.abxDrug ? `${ORD[S.abxDrug]}（${clock(S.abxAt ?? S.t)}）${S.cultureBeforeAbx === false ? "，culture 在抗生素之後" : ""}` : "沒有"}`,
      `Antibiotics: ${S.abxDrug ? `${ORD[S.abxDrug]} (${clock(S.abxAt ?? S.t)})${S.cultureBeforeAbx === false ? ", cultures drawn after antibiotics" : ""}` : "none"}`,
    ),
    tr(
      `輸液：${S.fluidAt !== null ? clock(S.fluidAt) : "沒有"}`,
      `IV fluids: ${S.fluidAt !== null ? clock(S.fluidAt) : "none"}`,
    ),
    tr(
      `阿凱同意 workup：${S.rApprovedAt !== null ? clock(S.rApprovedAt) : "沒有"}`,
      `Kai approved the workup: ${S.rApprovedAt !== null ? clock(S.rApprovedAt) : "no"}`,
    ),
    tr(
      `晨會：${S.confOpenAt !== null ? `有出席（第一句：${S.headline === "lead" ? "講 leak" : S.headline === "buried" ? "有講但不是第一句" : "沒講到"}）` : S.rPresented ? "沒出席，阿凱代報" : "沒有"}`,
      `Morning conference: ${S.confOpenAt !== null ? `attended (first sentence: ${S.headline === "lead" ? "led with the leak" : S.headline === "buried" ? "mentioned it, but not first" : "didn't mention it"})` : S.rPresented ? "absent; Kai presented instead" : "no"}`,
    ),
    tr(
      `主任決定處置：${S.acceptedAt !== null ? clock(S.acceptedAt) : "沒有"}`,
      `Attending decided management: ${S.acceptedAt !== null ? clock(S.acceptedAt) : "no"}`,
    ),
    tr(
      `休克：${S.shockAt !== null ? clock(S.shockAt) : "沒有"}`,
      `Shock: ${S.shockAt !== null ? clock(S.shockAt) : "no"}`,
    ),
    tr(
      `志偉衛教：${[...S.taught].map((k) => TEACH[k]).join("、") || "沒有"}${S.dischargeBy === "np" ? "（專師代講）" : ""}`,
      `Chih-Wei's teaching: ${[...S.taught].map((k) => TEACH[k]).join(", ") || "none"}${S.dischargeBy === "np" ? " (the NP did it instead)" : ""}`,
    ),
    tr(
      `蔡先生 Entecavir：${S.etvFixed ? "有發現" : "沒發現"}`,
      `Mr. Tsai's entecavir: ${S.etvFixed ? "caught" : "missed"}`,
    ),
    tr(`手部衛生疏失：${S.handSlips} 次`, `Hand-hygiene lapses: ${S.handSlips}`),
  ],
  known,
};
